// LibraryV2.jsx — Iter 62ea (Rayan msg 2026-02-12)
//
// Nouveau module Bibliothèque (remplace Notes + Library historiques).
// Design system ASA existant : navy #0B1330, bleu électrique #3E7BFA, Sora,
// glassmorphism, pas d'italique, pas de nouvelle palette.
//
// MVP :
//  - Onglets Notes / Fichiers (Fichiers = stub)
//  - Hub avec 6 tuiles catégorie + recherche globale avec suggestions
//  - Intérieur catégorie : toggle Client/Interne, sous-dossiers en grid, notes en cards
//  - Création de note manuelle (titre + contenu + catégorie + mode + sous-dossier)
//  - Édition inline via modale
//  - Tri alphabétique + Récemment utilisés + masquage dossiers vides (toggle)
//
// À venir (full brief) : simulation Google Docs, IA classement, résumé IA,
// drag&drop entre sous-dossiers, barre de recherche IA persistante.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Search, Plus, X, ArrowLeft, Users, Settings2, FileText, Folder, MoreHorizontal, Trash2, Sparkles, ExternalLink, Link2, ChevronRight, RefreshCw, Mic, MicOff, Upload } from 'lucide-react';
import { DndContext, useDroppable, useDraggable, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';

// ─── Design tokens ASA (identiques aux autres pages) ─────────────────────────
const NAVY       = '#0B1330';
const SURFACE    = 'rgba(20, 29, 66, 0.62)';
const SURFACE2   = 'rgba(28, 38, 80, 0.55)';
const BORDER     = 'rgba(62, 123, 250, 0.16)';
const BORDER_S   = 'rgba(62, 123, 250, 0.30)';
const BLUE       = '#3E7BFA';
const BLUE_LIGHT = '#5BBEFC';
const T1 = '#F1F4F9';
const T2 = 'rgba(200,215,240,0.72)';
const T3 = 'rgba(200,215,240,0.42)';

const CATEGORIES = [
  // Iter 62ec — Match des vrais dossiers Google Drive Elevation (Rayan msg 2026-02-13).
  { id: 'process_interne',    label: 'Process Interne',    subtitle: 'Nos façons de bosser en interne' },
  { id: 'process_externe',    label: 'Process externe',    subtitle: 'Ce qu\'on livre aux clients' },
  { id: 'contenu_interne',    label: 'Contenue interne',   subtitle: 'Ce qu\'on publie pour nous' },
  { id: 'developpement',      label: 'Développement',      subtitle: 'Idées produit, R&D, outils' },
  { id: 'client_consulting',  label: 'Client Consulting',  subtitle: 'Missions de conseil client' },
  { id: 'client_v2',          label: 'Client V2',          subtitle: 'Portefeuille clients V2' },
];

function catLabel(id) { return CATEGORIES.find((c) => c.id === id)?.label || id; }

// ─── Recent folders (localStorage, LIFO, max 30) ─────────────────────────────
// Iter 62el — Hugo msg : ajouter les 30 derniers dossiers utilisés pour retrouver
// vite un dossier sans passer par l'IA (qui prend 5-15s le temps du deep-scan).
const RECENT_FOLDERS_KEY = 'asa_recent_folders_v1';
const MAX_RECENT_FOLDERS = 30;

function readRecentFolders() {
  try {
    const raw = localStorage.getItem(RECENT_FOLDERS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function pushRecentFolder(folder) {
  // folder: {id, name, webViewLink, category, category_label, path}
  if (!folder || !folder.id) return;
  try {
    const list = readRecentFolders().filter((f) => f.id !== folder.id);
    list.unshift({ ...folder, ts: Date.now() });
    localStorage.setItem(RECENT_FOLDERS_KEY, JSON.stringify(list.slice(0, MAX_RECENT_FOLDERS)));
    // Emit event so open hub refreshes if visible
    try { window.dispatchEvent(new CustomEvent('asa:recent-folders-updated')); } catch {}
  } catch {}
}

// Iter 62ee — Rayan msg : "l'animation doit se faire max 2 fois par session,
// après c'est chiant". On stocke un compteur dans sessionStorage (reset au logout).
function useAnimateOnce(key, maxCount = 2) {
  return useMemo(() => {
    try {
      const k = `libAnim_${key}`;
      const cur = parseInt(sessionStorage.getItem(k) || '0', 10);
      if (cur >= maxCount) return false;
      sessionStorage.setItem(k, String(cur + 1));
      return true;
    } catch { return true; }
  }, [key, maxCount]);
}
function animClass(shouldAnimate, cls) { return shouldAnimate ? cls : ''; }

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) + ' à ' +
           d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

// ─── DictationButton : Web Speech API (dictation navigateur) ─────────────────
function DictationButton({ onTranscript }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const supported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const toggle = () => {
    if (!supported) {
      toast.error('Ton navigateur ne supporte pas la dictation vocale (essaie Chrome/Edge)');
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.lang = 'fr-FR';
    rec.continuous = false;
    rec.interimResults = false;
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.onresult = (event) => {
      const transcript = Array.from(event.results).map((r) => r[0].transcript).join(' ').trim();
      if (transcript) onTranscript(transcript);
    };
    recognitionRef.current = rec;
    try { rec.start(); } catch {}
  };

  return (
    <button
      onClick={toggle}
      data-testid="lib-dictation-btn"
      title={listening ? 'Arrêter la dictation' : 'Dicter (fr-FR)'}
      style={{
        position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
        width: 34, height: 34, borderRadius: 10,
        background: listening ? 'rgba(239,68,68,0.18)' : 'rgba(91,190,252,0.08)',
        border: `1px solid ${listening ? 'rgba(239,68,68,0.42)' : 'rgba(91,190,252,0.22)'}`,
        color: listening ? '#FF7A7A' : BLUE_LIGHT,
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2,
        transition: 'all 0.2s',
        animation: listening ? 'pulse 1.2s ease-in-out infinite' : 'none',
      }}
    >
      {listening ? <MicOff size={14} strokeWidth={2.4} /> : <Mic size={14} strokeWidth={2.4} />}
    </button>
  );
}

function formatRelativeDate(iso) {
  if (!iso) return 'récemment';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "à l'instant";
    if (min < 60) return `il y a ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `il y a ${h} h`;
    const days = Math.round(h / 24);
    if (days === 1) return 'hier';
    if (days < 7) return `il y a ${days} j`;
    if (days < 30) return `il y a ${Math.round(days / 7)} sem`;
    if (days < 365) return `il y a ${Math.round(days / 30)} mois`;
    return `il y a ${Math.round(days / 365)} an${days > 730 ? 's' : ''}`;
  } catch { return 'récemment'; }
}

// ─── Page racine ─────────────────────────────────────────────────────────────
export default function LibraryV2() {
  const [tab, setTab] = useState('notes');       // 'notes' | 'files'
  const [category, setCategory] = useState(null); // active category id or null (=hub)
  const [stats, setStats] = useState({ counts: {} });
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [showAiNoteModal, setShowAiNoteModal] = useState(false);
  const [driveConnected, setDriveConnected] = useState(false);
  const [me, setMe] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [filingPrompt, setFilingPrompt] = useState(null); // recent doc awaiting filing

  // Auto-open Mapping modal on OAuth return (?drive=ok) OR when Drive
  // is connected but no mapping exists yet (first-time UX)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('drive') === 'ok') {
      setShowMappingModal(true);
      // Clean the URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('drive') === 'error') {
      toast.error(`Erreur OAuth Google: ${params.get('reason') || 'inconnue'}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!driveConnected) return;
    const alreadyPrompted = sessionStorage.getItem('lib_mapping_prompted_v1');
    if (alreadyPrompted) return;
    // Check if any mapping exists
    api.get('/library/v2/drive/mapping').then((r) => {
      const mapping = r.data?.mapping || {};
      if (Object.keys(mapping).length === 0) {
        setShowMappingModal(true);
        sessionStorage.setItem('lib_mapping_prompted_v1', '1');
      }
    }).catch(() => {});
  }, [driveConnected]);

  // Filing prompt: on mount + on tab visibility change, check sessionStorage
  useEffect(() => {
    const check = () => {
      try {
        const raw = sessionStorage.getItem('lib_recent_doc');
        if (!raw) return;
        const d = JSON.parse(raw);
        // Skip if too old (>2h) or too fresh (<20s → user just opened, prob still editing)
        const ageMin = (Date.now() - (d.opened_at || 0)) / 60000;
        if (ageMin < 0.33 || ageMin > 120) return;
        if (d.filed) return; // already filed
        setFilingPrompt(d);
      } catch {}
    };
    check();
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshTick]);

  useEffect(() => {
    api.get('/me').then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);

  const reloadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/library/v2/stats');
      setStats(data || { counts: {} });
    } catch {}
  }, []);
  useEffect(() => { reloadStats(); }, [reloadStats]);

  const goToCategory = (catId) => { setCategory(catId); };
  const backToHub = () => { setCategory(null); };

  return (
    <div style={{ minHeight: '100vh', background: NAVY, position: 'relative' }}>
      {/* Grain overlay léger */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', opacity: 0.06,
        background: 'radial-gradient(circle at 20% 10%, rgba(62,123,250,0.35) 0%, transparent 45%), radial-gradient(circle at 80% 90%, rgba(91,190,252,0.20) 0%, transparent 40%)',
        zIndex: 0,
      }} />

      {/* Tabs Notes/Fichiers + status Drive en top-right (float) */}
      <TabsFloating
        tab={tab}
        setTab={setTab}
        onOpenMapping={() => setShowMappingModal(true)}
        onOpenAiNote={() => setShowAiNoteModal(true)}
        onDriveStatusChange={setDriveConnected}
        isFounder={me?.role === 'founder'}
      />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1400, margin: '0 auto', padding: '28px 32px 80px' }}>
        {tab === 'notes' && (
          category === null
            ? <HubView
                stats={stats}
                onSelectCategory={goToCategory}
                driveConnected={driveConnected}
                onOpenMapping={() => setShowMappingModal(true)}
                onOpenAiNote={() => setShowAiNoteModal(true)}
              />
            : <DriveCategoryView
                category={category}
                onBack={backToHub}
                onOpenMapping={() => setShowMappingModal(true)}
                viewMode="notes"
              />
        )}

        {tab === 'files' && (
          category === null
            ? <HubView
                stats={stats}
                onSelectCategory={goToCategory}
                driveConnected={driveConnected}
                onOpenMapping={() => setShowMappingModal(true)}
                onOpenAiNote={() => setShowAiNoteModal(true)}
                filesMode
              />
            : <DriveCategoryView
                category={category}
                onBack={backToHub}
                onOpenMapping={() => setShowMappingModal(true)}
                viewMode="files"
              />
        )}
      </div>

      {showMappingModal && (
        <MappingModal
          onClose={() => setShowMappingModal(false)}
          isFounder={me?.role === 'founder'}
        />
      )}

      {showAiNoteModal && (
        <AiNoteModal
          onClose={() => setShowAiNoteModal(false)}
          onCreated={() => {
            setShowAiNoteModal(false);
            setRefreshTick((t) => t + 1);
          }}
        />
      )}

      {driveConnected && <AsaAiChatFab />}

      {filingPrompt && (
        <FilingPromptModal
          doc={filingPrompt}
          onClose={() => {
            // Dismiss (mark as filed to prevent re-showing)
            try {
              const raw = sessionStorage.getItem('lib_recent_doc');
              if (raw) {
                const d = JSON.parse(raw);
                d.filed = true;
                sessionStorage.setItem('lib_recent_doc', JSON.stringify(d));
              }
            } catch {}
            setFilingPrompt(null);
          }}
          onFiled={() => {
            try { sessionStorage.removeItem('lib_recent_doc'); } catch {}
            setFilingPrompt(null);
            setRefreshTick((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}

// ─── Tabs floating (top-right) + bouton connect Drive ────────────────────────
function TabsFloating({ tab, setTab, onOpenMapping, onOpenAiNote, onDriveStatusChange, isFounder }) {
  const [driveStatus, setDriveStatus] = useState({ loading: true, connected: false, email: null });
  useEffect(() => {
    api.get('/drive/status').then((r) => {
      const connected = !!r.data?.connected;
      setDriveStatus({ loading: false, connected, email: r.data?.email || null });
      onDriveStatusChange && onDriveStatusChange(connected);
      // Warmup cache in background (fire-and-forget) so category views are instant
      if (connected) {
        api.post('/library/v2/drive/warmup').catch(() => {});
      }
    }).catch(() => {
      setDriveStatus({ loading: false, connected: false, email: null });
      onDriveStatusChange && onDriveStatusChange(false);
    });
  }, [onDriveStatusChange]);
  const connect = async () => {
    try {
      const r = await api.get('/drive/connect');
      const url = r.data?.auth_url || r.data?.url;
      if (url) {
        // Iter 62em — Ouvre l'OAuth Google dans un nouvel onglet au lieu de
        // rediriger toute l'app. Si Google renvoie une erreur (ex: redirect_uri
        // _mismatch en prod), la session ASA reste intacte — Hugo ferme juste
        // l'onglet Google. Sinon, on refetch le status au retour.
        toast.info('Autorise Google Drive dans le nouvel onglet — puis reviens ici, ça se rafraîchira tout seul.');
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('URL OAuth manquante — vérifie la config Google');
        console.warn('drive/connect response', r.data);
      }
    } catch (e) {
      toast.error(`Erreur OAuth Google: ${e?.response?.data?.detail || e.message || 'inconnue'}`);
      console.error(e);
    }
  };

  // Iter 62em — Auto-refresh du status Drive quand Hugo revient sur l'onglet ASA
  // (après avoir autorisé ou fermé l'onglet Google OAuth).
  useEffect(() => {
    const refreshOnFocus = () => {
      if (document.visibilityState !== 'visible') return;
      api.get('/drive/status').then((r) => {
        const wasConnected = driveStatus.connected;
        const nowConnected = !!r.data?.connected;
        setDriveStatus({ loading: false, connected: nowConnected, email: r.data?.email || null });
        onDriveStatusChange && onDriveStatusChange(nowConnected);
        if (!wasConnected && nowConnected) toast.success('Google Drive connecté !');
      }).catch(() => {});
    };
    const onMessage = (e) => {
      if (e && e.data && e.data.type === 'asa:drive-connected') {
        refreshOnFocus();
      }
    };
    document.addEventListener('visibilitychange', refreshOnFocus);
    window.addEventListener('focus', refreshOnFocus);
    window.addEventListener('message', onMessage);
    return () => {
      document.removeEventListener('visibilitychange', refreshOnFocus);
      window.removeEventListener('focus', refreshOnFocus);
      window.removeEventListener('message', onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driveStatus.connected]);
  const disconnect = async () => {
    if (!confirm('Déconnecter Google Drive ?')) return;
    try {
      await api.delete('/drive/disconnect');
      setDriveStatus({ loading: false, connected: false, email: null });
      onDriveStatusChange && onDriveStatusChange(false);
      toast.success('Drive déconnecté');
    } catch (e) {
      toast.error(`Erreur: ${e?.response?.data?.detail || e.message}`);
    }
  };
  const wipeAll = async () => {
    if (!confirm('⚠️ Supprimer TOUTES les notes de la Bibliothèque (V2 + anciennes) ? Action irréversible.')) return;
    try {
      const r = await api.delete('/library/v2/wipe');
      toast.success(`Wipe OK — ${r.data.notes_deleted} notes + ${r.data.subfolders_deleted} sous-dossiers supprimés`);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(`Erreur wipe: ${e?.response?.data?.detail || e.message}`);
    }
  };
  return (
    <div style={{ position: 'fixed', top: 28, right: 32, zIndex: 5, display: 'flex', gap: 10, alignItems: 'center' }}>
      {!driveStatus.loading && !driveStatus.connected && (
        <button
          onClick={connect}
          data-testid="lib-connect-drive-btn"
          className="glass-pill glass-apple-hover"
          style={{
            padding: '9px 16px', borderRadius: 12, cursor: 'pointer',
            color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <Sparkles size={13} color={BLUE_LIGHT} /> Connecter Google Drive
        </button>
      )}
      {!driveStatus.loading && driveStatus.connected && (
        <>
          <button
            onClick={onOpenMapping}
            data-testid="lib-open-mapping-btn"
            className="glass-pill glass-apple-hover"
            title="Configurer le lien entre les catégories ASA et les dossiers Drive"
            style={{
              padding: '9px 10px', borderRadius: 12, cursor: 'pointer',
              color: T1, display: 'flex', alignItems: 'center',
            }}
          >
            <Link2 size={14} color={BLUE_LIGHT} />
          </button>
          <div
            className="glass-pill"
            onClick={disconnect}
            title={`Compte Drive : ${driveStatus.email || 'inconnu'} — clique pour déconnecter`}
            data-testid="lib-drive-connected-badge"
            style={{
              padding: '9px 10px', borderRadius: 11,
              display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 4, background: '#7EE787', boxShadow: '0 0 6px #7EE78788', flexShrink: 0 }} />
          </div>
        </>
      )}
      {isFounder && driveStatus.connected && (
        <button
          onClick={wipeAll}
          data-testid="lib-wipe-btn"
          className="glass-pill glass-apple-hover"
          title="Vider TOUTES les notes DB (admin only)"
          style={{
            padding: '9px 10px', borderRadius: 12, cursor: 'pointer',
            color: '#FF7A7A',
            display: 'flex', alignItems: 'center',
          }}
        >
          <Trash2 size={12} />
        </button>
      )}
      <div className="glass-pill" style={{ display: 'flex', borderRadius: 14, padding: 4 }}>
        {['notes', 'files'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`lib-tab-${t}`}
            style={{
              padding: '8px 20px', borderRadius: 10,
              background: tab === t ? `linear-gradient(180deg, rgba(255,255,255,0.18) 0%, ${BLUE} 100%)` : 'transparent',
              color: tab === t ? '#F8FBFF' : T2,
              border: tab === t ? '1px solid rgba(255,255,255,0.20)' : '1px solid transparent',
              cursor: 'pointer',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
              transition: 'all 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
              boxShadow: tab === t ? 'inset 0 1px 0 rgba(255,255,255,0.25), 0 8px 20px -6px rgba(62,123,250,0.55)' : 'none',
            }}
          >{t === 'notes' ? 'Notes' : 'Fichiers'}</button>
        ))}
      </div>
    </div>
  );
}

// ─── Hub view centré (comme le mockup Elevation) ─────────────────────────────

function RecentFoldersStrip() {
  const [items, setItems] = useState(() => readRecentFolders());
  useEffect(() => {
    const onUpdate = () => setItems(readRecentFolders());
    window.addEventListener('asa:recent-folders-updated', onUpdate);
    window.addEventListener('storage', onUpdate);
    return () => {
      window.removeEventListener('asa:recent-folders-updated', onUpdate);
      window.removeEventListener('storage', onUpdate);
    };
  }, []);
  if (!items.length) return null;
  return (
    <div
      data-testid="lib-recent-folders-strip"
      style={{
        width: '100%', maxWidth: 1120, marginTop: 24,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        fontFamily: 'Sora', fontWeight: 700, fontSize: 11, letterSpacing: '0.10em',
        color: T3, textTransform: 'uppercase',
      }}>
        <RefreshCw size={11} />
        <span>Récemment utilisés ({items.length})</span>
        <button
          onClick={() => { try { localStorage.removeItem(RECENT_FOLDERS_KEY); } catch {} setItems([]); }}
          data-testid="lib-recent-folders-clear"
          title="Effacer l'historique"
          style={{
            marginLeft: 'auto', background: 'transparent', border: 'none',
            color: T3, cursor: 'pointer', fontSize: 10.5, fontFamily: 'Sora',
            letterSpacing: '0.06em',
          }}
        >Effacer</button>
      </div>
      <div style={{
        display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6,
        scrollbarWidth: 'thin', scrollbarColor: `${BLUE} transparent`,
      }}>
        {items.map((f) => (
          <button
            key={f.id}
            onClick={() => {
              pushRecentFolder(f); // bump to front
              window.open(f.webViewLink, '_blank');
            }}
            data-testid={`lib-recent-folder-${f.id}`}
            className="glass-apple glass-apple-hover"
            title={`${f.category_label || ''} · ${(f.path && f.path.length) ? f.path.join(' / ') + ' / ' : ''}${f.name}`}
            style={{
              flexShrink: 0, minWidth: 190, maxWidth: 240,
              padding: '10px 12px', borderRadius: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
              color: T1,
            }}
          >
            <div style={{
              width: 28, height: 28, borderRadius: 7, flexShrink: 0,
              background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 14px -6px rgba(62,123,250,0.55)',
            }}><Folder size={13} color="#F8FBFF" strokeWidth={2.4} /></div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
              <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10, color: T3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {f.category_label || ''}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function HubView({ stats, onSelectCategory, driveConnected, onOpenMapping, onOpenAiNote, filesMode }) {
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [driveStats, setDriveStats] = useState({}); // {category: {folders, docs, files}}
  const [driveStatsError, setDriveStatsError] = useState(null); // 401 = disconnected, 500 = error
  const [savedMappings, setSavedMappings] = useState({}); // mapping cache from DB (works even offline)
  const debounceRef = useRef(null);
  // Animation : max 2 fois par session sur le hub
  const shouldAnimate = useAnimateOnce('hub', 2);

  useEffect(() => {
    // Always fetch mappings from DB (works even without Drive)
    api.get('/library/v2/drive/mapping').then((r) => setSavedMappings(r.data?.mapping || {})).catch(() => {});
    if (!driveConnected) { setDriveStats({}); setDriveStatsError(null); return; }
    api.get('/library/v2/drive/stats/all')
      .then((r) => { setDriveStats(r.data?.stats || {}); setDriveStatsError(null); })
      .catch((e) => {
        setDriveStats({});
        setDriveStatsError(e?.response?.status || 'error');
      });
  }, [driveConnected]);

  const [aiSearchAnswer, setAiSearchAnswer] = useState(null);
  const [aiSearchMatches, setAiSearchMatches] = useState([]);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);

  // Iter 62el — Recherche LOCALE instantanée (zéro latence) sur les dossiers
  // récents + catégories. S'affiche AVANT l'IA pour que Hugo trouve son doss
  // en < 50ms sans attendre les 5-15s du deep-scan + Claude.
  const localMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const norm = (s) => (s || '').toLowerCase();
    const recents = readRecentFolders();
    const scored = [];
    // Score : startsWith (2) > includes (1). Recent bonus +0.5.
    for (const f of recents) {
      const n = norm(f.name);
      if (n.startsWith(q)) scored.push({ type: 'folder', item: f, score: 2.5 });
      else if (n.includes(q)) scored.push({ type: 'folder', item: f, score: 1.5 });
    }
    // Match sur les catégories elles-mêmes
    for (const cat of CATEGORIES) {
      const n = norm(cat.label);
      if (n.startsWith(q)) scored.push({ type: 'category', item: cat, score: 2 });
      else if (n.includes(q)) scored.push({ type: 'category', item: cat, score: 1 });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 8);
  }, [search]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!search.trim() || !driveConnected) {
      setSuggestions([]);
      setAiSearchAnswer(null);
      setAiSearchMatches([]);
      setAiSearchLoading(false);
      return;
    }
    // Show loading immediately (before debounce fires) — so user sees the spinner instantly
    setAiSearchLoading(true);
    setAiSearchAnswer(null);
    setAiSearchMatches([]);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post('/library/v2/ai/search', { query: search.trim() });
        setAiSearchAnswer(data.answer || null);
        setAiSearchMatches(data.matches || []);
        setSuggestions(data.matches || []);
      } catch {
        setAiSearchAnswer(null);
        setAiSearchMatches([]);
        setSuggestions([]);
      } finally {
        setAiSearchLoading(false);
      }
    }, 500);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, driveConnected]);

  const grouped = useMemo(() => {
    const g = {};
    for (const n of suggestions) {
      const k = n.lib_category || 'autre';
      (g[k] = g[k] || []).push(n);
    }
    for (const k of Object.keys(g)) g[k].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    return g;
  }, [suggestions]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22, paddingTop: 80 }}>
      {/* Titre + sous-titre centrés — animation blur-up (max 2× par session) */}
      <div className={animClass(shouldAnimate, 'blur-up')} style={{ textAlign: 'center' }}>
        <h1 style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 46, color: T1, margin: 0,
          letterSpacing: '-0.03em', lineHeight: 1.1,
        }}>{filesMode ? 'Fichiers' : 'Bibliothèque'}</h1>
        <div style={{ marginTop: 10, fontFamily: 'Sora', fontWeight: 500, fontSize: 15, color: T3 }}>
          {filesMode ? 'Vidéos, images, PDFs — organisés par catégorie.' : 'Veuillez choisir une catégorie.'}
        </div>
      </div>

      {/* Search bar centrale — glass Apple */}
      <div className={animClass(shouldAnimate, 'blur-up')} style={{ position: 'relative', width: '100%', maxWidth: 720, animationDelay: '0.08s', zIndex: 30 }}>
        <Search size={18} style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', color: T3, zIndex: 1 }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setShowSug(true)}
          onBlur={() => setTimeout(() => setShowSug(false), 200)}
          placeholder="Demande à l'IA ASA : « Où est la note X ? »"
          data-testid="lib-global-search"
          className="glass-apple"
          style={{
            width: '100%', padding: '17px 60px 17px 52px', borderRadius: 18,
            color: T1, outline: 'none',
            fontFamily: 'Sora', fontWeight: 500, fontSize: 14.5,
            transition: 'border-color 0.25s',
          }}
          onFocusCapture={(e) => e.currentTarget.style.borderColor = 'rgba(91,190,252,0.35)'}
          onBlurCapture={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'}
        />
        <DictationButton
          onTranscript={(text) => {
            setSearch((s) => s ? `${s} ${text}` : text);
            setShowSug(true);
          }}
        />
        {showSug && search.trim() && (aiSearchLoading || aiSearchAnswer || aiSearchMatches.length > 0 || localMatches.length > 0) && (
          <div className="glass-apple-strong scale-in" style={{
            position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
            borderRadius: 16,
            maxHeight: 520, overflowY: 'auto', zIndex: 20, padding: 12,
            background: 'linear-gradient(180deg, rgba(11,19,48,0.94) 0%, rgba(11,19,48,0.88) 100%)',
          }}>
            {/* ── Résultats INSTANTANÉS (dossiers récents + catégories) ─ */}
            {localMatches.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: T3, letterSpacing: '0.10em', textTransform: 'uppercase', padding: '2px 4px 4px' }}>
                  Résultats rapides
                </div>
                {localMatches.map((m, i) => {
                  if (m.type === 'folder') {
                    const f = m.item;
                    return (
                      <button
                        key={`local-f-${f.id}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          pushRecentFolder(f);
                          window.open(f.webViewLink, '_blank');
                          setShowSug(false); setSearch('');
                        }}
                        data-testid={`lib-local-folder-${f.id}`}
                        className="glass-apple-hover"
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          width: '100%', textAlign: 'left',
                          padding: '10px 12px', borderRadius: 10,
                          background: 'rgba(91,190,252,0.06)', border: '1px solid rgba(91,190,252,0.20)',
                          cursor: 'pointer', color: T1,
                        }}
                      >
                        <Folder size={14} color={BLUE_LIGHT} />
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                          <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {f.category_label || ''}
                          </div>
                        </div>
                        <ExternalLink size={11} color={T3} />
                      </button>
                    );
                  }
                  // category
                  const c = m.item;
                  return (
                    <button
                      key={`local-c-${c.id}-${i}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onSelectCategory(c.id);
                        setShowSug(false); setSearch('');
                      }}
                      data-testid={`lib-local-category-${c.id}`}
                      className="glass-apple-hover"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        width: '100%', textAlign: 'left',
                        padding: '10px 12px', borderRadius: 10,
                        background: 'rgba(62,123,250,0.06)', border: '1px solid rgba(62,123,250,0.20)',
                        cursor: 'pointer', color: T1,
                      }}
                    >
                      <Folder size={14} color={BLUE} />
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13 }}>{c.label}</div>
                        <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 2 }}>Catégorie ASA</div>
                      </div>
                      <ChevronRight size={12} color={T3} />
                    </button>
                  );
                })}
              </div>
            )}
            {aiSearchLoading && (
              <div style={{ padding: '18px 14px', textAlign: 'center', fontFamily: 'Sora', fontSize: 12, color: T3 }}>
                <Sparkles size={13} style={{ marginRight: 6, verticalAlign: 'middle', color: BLUE_LIGHT }} />
                IA ASA cherche dans tes docs…
              </div>
            )}
            {!aiSearchLoading && aiSearchAnswer && (
              <div style={{
                padding: '11px 14px', marginBottom: 10, borderRadius: 12,
                background: 'rgba(91,190,252,0.10)', border: '1px solid rgba(91,190,252,0.22)',
                fontFamily: 'Sora', fontSize: 12.5, color: T1, lineHeight: 1.5,
              }}>
                <Sparkles size={12} style={{ marginRight: 6, verticalAlign: 'middle', color: BLUE_LIGHT }} />
                {aiSearchAnswer}
              </div>
            )}
            {!aiSearchLoading && aiSearchMatches.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {aiSearchMatches.map((m) => (
                  <button
                    key={m.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      // Track for filing prompt
                      try {
                        sessionStorage.setItem('lib_recent_doc', JSON.stringify({
                          id: m.id, name: m.name, webViewLink: m.webViewLink,
                          category: null, opened_at: Date.now(),
                        }));
                      } catch {}
                      window.open(m.webViewLink, '_blank');
                      setShowSug(false); setSearch('');
                    }}
                    data-testid={`lib-search-result-${m.id}`}
                    className="glass-apple-hover"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', textAlign: 'left',
                      padding: '10px 12px', borderRadius: 10,
                      background: 'rgba(66,133,244,0.06)', border: '1px solid rgba(66,133,244,0.20)',
                      cursor: 'pointer', color: T1,
                    }}
                  >
                    <FileText size={14} color="#4285F4" />
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
                      <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        📁 {m.category_label} — {m.path}
                      </div>
                      {m.reason && (
                        <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: BLUE_LIGHT, marginTop: 3, fontStyle: 'italic' }}>
                          ✨ {m.reason}
                        </div>
                      )}
                    </div>
                    <ExternalLink size={11} color={T3} />
                  </button>
                ))}
              </div>
            )}
            {!aiSearchLoading && !aiSearchAnswer && aiSearchMatches.length === 0 && localMatches.length === 0 && (
              <div style={{ padding: '18px 14px', textAlign: 'center', fontFamily: 'Sora', fontSize: 12, color: T3, fontStyle: 'italic' }}>
                Aucun doc trouvé.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bouton "+ Nouvelle note" centré — caché quand le dropdown de recherche est ouvert (évite le chevauchement) */}
      {driveConnected ? (
        <button
          onClick={onOpenAiNote}
          data-testid="lib-hub-new-note-btn"
          className={`${animClass(shouldAnimate, 'blur-up')} glass-apple-hover`}
          style={{
            animationDelay: '0.14s',
            position: 'relative', zIndex: 1,
            display: (showSug && search.trim()) ? 'none' : 'flex',
            alignItems: 'center', gap: 8,
            padding: '13px 24px', borderRadius: 16,
            background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
            backdropFilter: 'blur(28px) saturate(200%)',
            WebkitBackdropFilter: 'blur(28px) saturate(200%)',
            color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)', cursor: 'pointer',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 14,
            boxShadow: '0 16px 40px -12px rgba(62,123,250,0.65), inset 0 1px 0 rgba(255,255,255,0.30)',
          }}
        >
          <Plus size={17} strokeWidth={2.8} /> Nouvelle note
        </button>
      ) : (
        <button
          onClick={async () => {
            // Iter 62em — trigger OAuth Drive dans un NOUVEL ONGLET (au lieu
            // d'ouvrir la modale de mapping qui plantait avec un 401 → logout).
            try {
              const r = await api.get('/drive/connect');
              const url = r.data?.auth_url || r.data?.url;
              if (url) {
                toast.info('Autorise Google Drive dans le nouvel onglet — reviens ici après, ça se rafraîchira tout seul.');
                window.open(url, '_blank', 'noopener,noreferrer');
              } else {
                toast.error('URL OAuth manquante — vérifie la config Google');
              }
            } catch (e) {
              toast.error(`Erreur OAuth : ${e?.response?.data?.detail || e.message || 'inconnue'}`);
            }
          }}
          data-testid="lib-hub-connect-drive-btn"
          className={`${animClass(shouldAnimate, 'blur-up')} glass-apple-hover`}
          style={{
            animationDelay: '0.14s',
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '13px 24px', borderRadius: 16,
            background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
            backdropFilter: 'blur(28px) saturate(200%)',
            WebkitBackdropFilter: 'blur(28px) saturate(200%)',
            color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)', cursor: 'pointer',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 14,
            boxShadow: '0 16px 40px -12px rgba(62,123,250,0.65), inset 0 1px 0 rgba(255,255,255,0.30)',
          }}
        >
          <Sparkles size={17} strokeWidth={2.6} /> Connecter les dossiers Drive
        </button>
      )}

      {/* Récemment utilisés (max 30) — zéro latence, localStorage. Masqué pendant la recherche pour éviter chevauchement avec le dropdown */}
      {driveConnected && !(showSug && search.trim()) && <RecentFoldersStrip />}

      {/* Grille 3×2 des 6 catégories — glass apple + stagger blur entry */}
      <div className={animClass(shouldAnimate, 'blur-up-stagger')} style={{
        marginTop: 16,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 20,
        width: '100%', maxWidth: 1120,
      }}>
        {CATEGORIES.map((cat, idx) => {
          const ds = driveStats[cat.id];
          const mapping = savedMappings[cat.id];
          const isMappedInDb = !!mapping;
          const hasStats = !!ds;
          return (
            <button
              key={cat.id}
              onClick={() => onSelectCategory(cat.id)}
              data-testid={`lib-cat-tile-${cat.id}`}
              className="glass-apple glass-apple-hover"
              style={{
                textAlign: 'center', padding: '28px 22px 24px', borderRadius: 24,
                cursor: 'pointer', color: T1,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                minHeight: 210,
              }}
            >
              <div style={{
                width: 60, height: 60, borderRadius: 18,
                background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 14px 30px -8px rgba(62,123,250,0.60),
                            inset 0 1px 0 rgba(255,255,255,0.35),
                            inset 0 -2px 0 rgba(0,0,0,0.15)`,
              }}>
                <Folder size={26} color="#F8FBFF" strokeWidth={2.2} />
              </div>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 18, color: T1 }}>{cat.label}</div>
              {!isMappedInDb && (
                <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5, color: T3, fontStyle: 'italic' }}>
                  Non lié à Drive — configure le mapping
                </div>
              )}
              {isMappedInDb && !hasStats && driveStatsError === 401 && (
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: '#FFB86B', padding: '4px 10px', borderRadius: 8, background: 'rgba(255, 184, 107, 0.10)', border: '1px solid rgba(255, 184, 107, 0.28)' }}>
                  Reconnecte Google Drive
                </div>
              )}
              {isMappedInDb && !hasStats && driveStatsError !== 401 && (
                <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5, color: T3, fontStyle: 'italic' }}>
                  Chargement…
                </div>
              )}
              {hasStats && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {filesMode ? (
                    <>
                      <TileStatChip color="#38BDF8" icon={<Sparkles size={11} strokeWidth={2.4} />} value={ds.files} label="fichier" />
                      <TileStatChip color={BLUE_LIGHT} icon={<Folder size={11} strokeWidth={2.4} />} value={ds.folders} label="dossier" />
                    </>
                  ) : (
                    <>
                      <TileStatChip color="#4285F4" icon={<FileText size={11} strokeWidth={2.4} />} value={ds.docs} label="doc" />
                      <TileStatChip color={BLUE_LIGHT} icon={<Folder size={11} strokeWidth={2.4} />} value={ds.folders} label="dossier" />
                    </>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TileStatChip({ color, icon, value, label }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 5,
      padding: '4px 9px', borderRadius: 8,
      background: `${color}18`, border: `1px solid ${color}30`,
      fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: color,
    }}>
      {icon}
      <span>{value}</span>
      <span style={{ opacity: 0.8, fontWeight: 500 }}>{label}{value > 1 ? 's' : ''}</span>
    </div>
  );
}

// ─── Category view (Client/Interne toggle + sous-dossiers/sections/notes) ────
function CategoryView({ category, mode, setMode, subfolder, setSubfolder, clientSection, setClientSection, onBack, onOpenNote, onNewNote }) {
  const [subfolders, setSubfolders] = useState([]);
  const [sections, setSections] = useState([]);      // sections dans un client sélectionné
  const [rootCount, setRootCount] = useState(0);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recent'); // 'alpha' | 'recent' — récentes par défaut
  const [showEmpty, setShowEmpty] = useState(false);
  const [showNewSubfolder, setShowNewSubfolder] = useState(false);
  const [dragging, setDragging] = useState(null); // {id, title} en cours de drag

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  // Iter 62ee — Animation blur-up max 2× par session sur cette vue (Rayan msg)
  const shouldAnimate = useAnimateOnce(`cat_${category}_${mode}_${subfolder || ''}_${clientSection || ''}`, 2);

  // Chargement principal
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // Toujours charger la liste des sous-dossiers (clients ou sections interne)
      const subsReq = api.get(`/library/v2/subfolders?category=${category}&mode=${mode}`);

      // Si on est dans un client (mode client + subfolder), charger les sections
      let sectionsReq = Promise.resolve({ data: { sections: [], root_count: 0 } });
      if (mode === 'client' && subfolder) {
        sectionsReq = api.get(`/library/v2/sections?category=${category}&client=${encodeURIComponent(subfolder)}`);
      }

      // Notes : dépend du niveau
      let notesReq;
      if (mode === 'client' && subfolder) {
        if (clientSection === '__root__') {
          notesReq = api.get(`/library/v2/notes?category=${category}&mode=client&subfolder=${encodeURIComponent(subfolder)}&section=`);
        } else if (clientSection) {
          notesReq = api.get(`/library/v2/notes?category=${category}&mode=client&subfolder=${encodeURIComponent(subfolder)}&section=${encodeURIComponent(clientSection)}`);
        } else {
          notesReq = api.get(`/library/v2/notes?category=${category}&mode=client&subfolder=${encodeURIComponent(subfolder)}`);
        }
      } else if (subfolder) {
        notesReq = api.get(`/library/v2/notes?category=${category}&mode=${mode}&subfolder=${encodeURIComponent(subfolder)}`);
      } else {
        notesReq = api.get(`/library/v2/notes?category=${category}&mode=${mode}`);
      }

      const [subs, secs, all] = await Promise.all([subsReq, sectionsReq, notesReq]);
      setSubfolders(subs.data || []);
      setSections(secs.data?.sections || []);
      setRootCount(secs.data?.root_count || 0);
      setNotes(all.data || []);
    } catch { setSubfolders([]); setSections([]); setNotes([]); }
    setLoading(false);
  }, [category, mode, subfolder, clientSection]);

  useEffect(() => { reload(); }, [reload]);

  const filteredSubs = useMemo(() => {
    let list = [...subfolders];
    if (!showEmpty) list = list.filter((s) => s.count > 0);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      if (sort === 'recent') return (b.last_used || '').localeCompare(a.last_used || '');
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [subfolders, sort, showEmpty, search]);

  const filteredSections = useMemo(() => {
    let list = [...sections];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      if (sort === 'recent') return (b.last_used || '').localeCompare(a.last_used || '');
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [sections, sort, search]);

  const filteredNotes = useMemo(() => {
    let list = [...notes];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((n) => (n.title || '').toLowerCase().includes(q) || (n.lib_summary || '').toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      if (sort === 'recent') return (b.updated_at || '').localeCompare(a.updated_at || '');
      return (a.title || '').localeCompare(b.title || '');
    });
    return list;
  }, [notes, sort, search]);

  // Iter 62ed — Rayan msg : "3 dernières notes récentes en haut, toutes les
  // autres en bas". Applicable au tri Récent + quand on est dans un sous-dossier
  // (section, sub interne) ou dans la vue client (all notes du client).
  const { recentNotes, restNotes } = useMemo(() => {
    if (sort !== 'recent' || filteredNotes.length <= 3) return { recentNotes: [], restNotes: filteredNotes };
    return {
      recentNotes: filteredNotes.slice(0, 3),
      restNotes: filteredNotes.slice(3),
    };
  }, [filteredNotes, sort]);

  const catCfg = CATEGORIES.find((c) => c.id === category);
  const emptySubCount = subfolders.filter((s) => s.count === 0).length;

  // ─── Drag & drop handlers ───────────────────────────────────────────────
  const handleDragStart = (event) => {
    const noteId = event.active?.id;
    const note = notes.find((n) => n.id === noteId);
    if (note) setDragging({ id: noteId, title: note.title });
  };
  const handleDragEnd = async (event) => {
    setDragging(null);
    const { active, over } = event;
    if (!over || !active) return;
    const noteId = active.id;
    const target = over.id; // format: "subfolder:X" | "section:X" | "root"
    const note = notes.find((n) => n.id === noteId);
    if (!note) return;
    let patch = null;
    if (typeof target === 'string' && target.startsWith('subfolder:')) {
      const newSub = target.slice('subfolder:'.length);
      if (newSub === note.lib_subfolder) return;
      patch = { subfolder: newSub, section: '' };
    } else if (typeof target === 'string' && target.startsWith('section:')) {
      const newSec = target.slice('section:'.length);
      if (newSec === (note.lib_section || '')) return;
      patch = { section: newSec };
    } else if (target === 'root') {
      if (!note.lib_section) return;
      patch = { section: '' };
    }
    if (!patch) return;
    try {
      await api.patch(`/library/v2/notes/${noteId}`, patch);
      toast.success(`Note déplacée${patch.subfolder ? ` vers ${patch.subfolder}` : (patch.section ? ` vers ${patch.section}` : ' à la racine')}`);
      reload();
    } catch { toast.error('Erreur déplacement'); }
  };

  // Contexte de vue : quel niveau on affiche
  const inClientRoot   = mode === 'client' && subfolder && !clientSection;
  const inClientSec    = mode === 'client' && subfolder && clientSection;
  const inInterneSub   = mode === 'interne' && subfolder;
  const atCategoryRoot = !subfolder;

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className={animClass(shouldAnimate, 'page-fade')}>
        {/* Breadcrumb + back */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              if (clientSection) setClientSection(null);
              else if (subfolder) setSubfolder(null);
              else onBack();
            }}
            data-testid="lib-back-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', borderRadius: 10,
              background: SURFACE, border: `1px solid ${BORDER}`, color: T2,
              cursor: 'pointer', fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5,
            }}
          ><ArrowLeft size={14} /> Retour</button>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: T3 }}>
            Bibliothèque · <span style={{ color: subfolder ? T2 : T1 }}>{catCfg?.label}</span>
            {subfolder && <> · <span style={{ color: clientSection ? T2 : T1 }}>{subfolder}</span></>}
            {clientSection && clientSection !== '__root__' && <> · <span style={{ color: T1 }}>{clientSection}</span></>}
            {clientSection === '__root__' && <> · <span style={{ color: T1 }}>Racine</span></>}
          </div>
        </div>

        {/* Toggle Client/Interne + tri (uniquement à la racine catégorie) */}
        {atCategoryRoot && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 3 }}>
              <ModeBtn active={mode === 'client'} onClick={() => setMode('client')} icon={<Users size={14} />} label="Client" testId="lib-mode-client" />
              <ModeBtn active={mode === 'interne'} onClick={() => setMode('interne')} icon={<Settings2 size={14} />} label="Interne" testId="lib-mode-interne" />
            </div>
            {mode === 'interne' && (
              <button
                onClick={() => setShowNewSubfolder(true)}
                data-testid="lib-new-subfolder-btn"
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 10,
                  background: 'transparent', border: `1px dashed ${BORDER_S}`, color: T2,
                  cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                }}
              ><Plus size={14} /> Nouveau sous-dossier</button>
            )}
            <div style={{ flex: 1 }} />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="lib-sort"
              className="glass-pill"
              style={{
                padding: '8px 14px', borderRadius: 12, cursor: 'pointer',
                color: T1,
                fontFamily: 'Sora', fontWeight: 600, fontSize: 12, outline: 'none',
              }}
            >
              <option value="recent">Récentes</option>
              <option value="alpha">A → Z</option>
            </select>
          </div>
        )}

        {/* Local search */}
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T3 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={clientSection ? `Rechercher dans ${clientSection}…` : (subfolder ? `Rechercher dans ${subfolder}…` : `Rechercher dans ${catCfg?.label}…`)}
            data-testid="lib-local-search"
            style={{
              width: '100%', padding: '10px 12px 10px 36px', borderRadius: 11,
              background: SURFACE2, border: `1px solid ${BORDER}`, color: T1,
              outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 13,
            }}
          />
        </div>

        {/* ─── Niveau : racine catégorie → sous-dossiers ────────────────────── */}
        {atCategoryRoot && (
          <>
            {loading ? (
              <SkeletonGrid count={6} height={72} />
            ) : (
              <div key={`subs-${mode}`} className={animClass(shouldAnimate, 'blur-up-stagger')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
                {filteredSubs.map((s) => (
                  <SubfolderCard
                    key={`${s.mode}-${s.name}`}
                    sub={s} mode={mode}
                    onOpen={() => setSubfolder(s.name)}
                    droppableId={`subfolder:${s.name}`}
                  />
                ))}
                {filteredSubs.length === 0 && (
                  <div style={{ gridColumn: '1 / -1', padding: 40, textAlign: 'center', color: T3, fontFamily: 'Sora', fontSize: 13 }}>
                    Aucun sous-dossier {search ? 'ne correspond à ta recherche' : 'pour l\'instant'}.
                  </div>
                )}
              </div>
            )}
            {!showEmpty && emptySubCount > 0 && (
              <button
                onClick={() => setShowEmpty(true)}
                data-testid="lib-show-empty-btn"
                className="glass-pill"
                style={{
                  marginTop: 14, padding: '9px 16px', borderRadius: 11, cursor: 'pointer',
                  color: T2, fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
                }}
              >Afficher les {emptySubCount} dossier{emptySubCount > 1 ? 's' : ''} vide{emptySubCount > 1 ? 's' : ''}</button>
            )}
          </>
        )}

        {/* ─── Niveau : intérieur d'un client → sections ─────────────────────── */}
        {inClientRoot && (
          <>
            {loading ? (
              <SkeletonGrid count={5} height={72} />
            ) : (
              <div key={`sec-${subfolder}`} className={animClass(shouldAnimate, 'blur-up-stagger')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 24 }}>
                {rootCount > 0 && (
                  <SubfolderCard
                    key="__root__"
                    sub={{ name: 'Racine', count: rootCount }} mode="section-root"
                    onOpen={() => setClientSection('__root__')}
                    droppableId="root"
                  />
                )}
                {filteredSections.map((s) => (
                  <SubfolderCard
                    key={s.name}
                    sub={s} mode="section"
                    onOpen={() => setClientSection(s.name)}
                    droppableId={`section:${s.name}`}
                  />
                ))}
                {rootCount === 0 && filteredSections.length === 0 && (
                  <div style={{ gridColumn: '1 / -1', padding: 40, textAlign: 'center', color: T3, fontFamily: 'Sora', fontSize: 13 }}>
                    Aucune note pour ce client pour l'instant.
                    <br />
                    <button onClick={onNewNote} data-testid="lib-empty-new-note-btn" style={{ marginTop: 12, padding: '9px 16px', borderRadius: 11, cursor: 'pointer', background: BLUE, border: 'none', color: '#F8FBFF', fontFamily: 'Sora', fontWeight: 700, fontSize: 12 }}>Créer une note</button>
                  </div>
                )}
              </div>
            )}
            {/* Aperçu de toutes les notes du client : Récentes + toutes les autres */}
            {!loading && filteredNotes.length > 0 && (
              <NotesGroupedView recentNotes={recentNotes} restNotes={restNotes} totalCount={filteredNotes.length} onOpenNote={onOpenNote} />
            )}
          </>
        )}

        {/* ─── Niveau : intérieur section OU sous-dossier interne → notes ───── */}
        {(inClientSec || inInterneSub) && (
          loading ? (
            <SkeletonGrid count={6} height={120} />
          ) : (
            <NotesGroupedView
              recentNotes={recentNotes} restNotes={restNotes}
              totalCount={filteredNotes.length}
              onOpenNote={onOpenNote}
              emptyAction={onNewNote}
              search={search}
            />
          )
        )}

        {showNewSubfolder && (
          <NewSubfolderModal
            category={category}
            onClose={() => setShowNewSubfolder(false)}
            onCreated={() => { setShowNewSubfolder(false); reload(); toast.success('Sous-dossier créé'); }}
          />
        )}
      </div>

      <DragOverlay dropAnimation={null}>
        {dragging && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: `linear-gradient(180deg, ${SURFACE} 0%, rgba(14,22,44,0.98) 100%)`,
            border: `1px solid ${BLUE}`, color: T1,
            fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
            boxShadow: '0 20px 40px -12px rgba(62,123,250,0.55)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <FileText size={14} color={BLUE_LIGHT} />
            <span>{dragging.title || 'Note'}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function ModeBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '7px 14px', borderRadius: 9, border: 'none', cursor: 'pointer',
        background: active ? BLUE : 'transparent',
        color: active ? NAVY : T2,
        fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
        transition: 'all 0.15s',
      }}
    >{icon} {label}</button>
  );
}

// ─── Skeleton loader (glass Apple) ───────────────────────────────────────────
function SkeletonGrid({ count = 6, height = 72 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(220px, 1fr))`, gap: 14 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="glass-apple" style={{
          height, borderRadius: 16, position: 'relative', overflow: 'hidden',
        }}>
          <div className="shimmer" style={{ position: 'absolute', inset: 0, opacity: 0.5 }} />
        </div>
      ))}
    </div>
  );
}

// ─── Notes groupées : "Récentes" + "Toutes les autres" ───────────────────────
function NotesGroupedView({ recentNotes, restNotes, totalCount, onOpenNote, emptyAction, search }) {
  const hasRecent = recentNotes && recentNotes.length > 0;
  const hasRest   = restNotes && restNotes.length > 0;
  if (!hasRecent && !hasRest) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: T3, fontFamily: 'Sora', fontSize: 13 }} className="blur-up">
        Aucune note {search ? 'ne correspond' : 'ici pour l\'instant'}.<br />
        {emptyAction && (
          <button
            onClick={emptyAction}
            data-testid="lib-empty-new-note-btn"
            className="glass-apple-hover"
            style={{
              marginTop: 14, padding: '10px 18px', borderRadius: 12, cursor: 'pointer',
              background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, ${BLUE} 100%)`,
              border: '1px solid rgba(255,255,255,0.20)', color: '#F8FBFF',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
              boxShadow: '0 12px 30px -8px rgba(62,123,250,0.55), inset 0 1px 0 rgba(255,255,255,0.25)',
            }}
          >Créer la première note</button>
        )}
      </div>
    );
  }
  return (
    <div>
      {hasRecent && (
        <div style={{ marginBottom: 22 }}>
          <div className="blur-up" style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: T2, letterSpacing: '0.02em',
          }}>
            <Sparkles size={14} color={BLUE_LIGHT} />
            <span style={{ color: T1 }}>Récentes</span>
            <span style={{ color: T3, fontWeight: 500 }}>· 3 dernières modifiées</span>
          </div>
          <div className="blur-up-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {recentNotes.map((n) => <NoteCard key={n.id} note={n} onOpen={() => onOpenNote(n)} />)}
          </div>
        </div>
      )}
      {hasRest && (
        <div>
          <div className="blur-up" style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, marginTop: hasRecent ? 4 : 0,
            fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: T2,
          }}>
            <span style={{ color: T1 }}>{hasRecent ? 'Toutes les autres' : 'Toutes les notes'}</span>
            <span style={{ color: T3, fontWeight: 500 }}>· {restNotes.length}</span>
          </div>
          <div className="blur-up-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {restNotes.map((n) => <NoteCard key={n.id} note={n} onOpen={() => onOpenNote(n)} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sous-dossier card (droppable) ───────────────────────────────────────────
function SubfolderCard({ sub, mode, onOpen, droppableId }) {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId || `sub-${sub.name}` });
  const icon = mode === 'client' ? <Users size={16} color="#F8FBFF" />
              : mode === 'section' ? <Folder size={16} color="#F8FBFF" />
              : mode === 'section-root' ? <FileText size={16} color="#F8FBFF" />
              : <Settings2 size={16} color="#F8FBFF" />;
  return (
    <button
      ref={setNodeRef}
      onClick={onOpen}
      data-testid={`lib-subfolder-${sub.name}`}
      className="glass-apple glass-apple-hover"
      style={{
        textAlign: 'left', padding: 18, borderRadius: 16, cursor: 'pointer',
        border: isOver ? '1px solid rgba(91,190,252,0.60)' : undefined,
        boxShadow: isOver ? 'inset 0 1px 0 rgba(255,255,255,0.20), 0 24px 48px -12px rgba(62,123,250,0.45)' : undefined,
        color: T1, display: 'flex', alignItems: 'center', gap: 12,
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 12, flexShrink: 0,
        background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28), 0 8px 18px -6px rgba(62,123,250,0.55)',
      }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14.5, color: T1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub.name}</div>
        <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5, color: T3, marginTop: 3 }}>
          {sub.count} note{sub.count > 1 ? 's' : ''}
        </div>
      </div>
    </button>
  );
}

// ─── Note card (draggable + miniature + résumé + 3 dates) ────────────────────
function NoteCard({ note, onOpen }) {
  const { setNodeRef, attributes, listeners, transform, isDragging } = useDraggable({ id: note.id });
  const style = {
    opacity: isDragging ? 0.4 : 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <button
        onClick={onOpen}
        data-testid={`lib-note-card-${note.id}`}
        className="glass-apple glass-apple-hover"
        style={{
          width: '100%', textAlign: 'left', padding: 16, borderRadius: 16, cursor: 'pointer',
          color: T1, display: 'flex', gap: 12,
        }}
      >
        {/* Miniature façon aperçu de document */}
        <div style={{
          width: 68, height: 84, borderRadius: 10, flexShrink: 0,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)',
          padding: 8, display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          {[100, 85, 92, 70, 88, 78].map((w, i) => (
            <div key={i} style={{
              height: 3, borderRadius: 2,
              width: `${w}%`,
              background: i === 0 ? BLUE_LIGHT : 'rgba(255,255,255,0.22)',
            }} />
          ))}
        </div>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14.5, color: T1, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {note.title || 'Sans titre'}
          </div>
          <div style={{
            fontFamily: 'Sora', fontWeight: 500, fontSize: 12, color: T2,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', marginBottom: 8, minHeight: 30,
          }}>
            {note.lib_summary || 'Aucun résumé pour l\'instant.'}
          </div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 2, fontFamily: 'Sora', fontSize: 10.5, color: T3, fontWeight: 500 }}>
            <div>Créée le {formatDate(note.created_at)}</div>
            <div>Dernière ouverture le {formatDate(note.last_opened_at || note.updated_at)}</div>
            <div>Dernière modification le {formatDate(note.updated_at)}</div>
          </div>
        </div>
      </button>
    </div>
  );
}

// ─── New note modal ──────────────────────────────────────────────────────────
function NewNoteModal({ onClose, initialCategory, initialMode, initialSubfolder, initialSection, onCreated }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState(initialCategory || 'contenu_interne');
  const [mode, setMode] = useState(initialMode || 'client');
  const [subfolder, setSubfolder] = useState(initialSubfolder || '');
  const [section, setSection] = useState(initialSection && initialSection !== '__root__' ? initialSection : '');
  const [availableSubs, setAvailableSubs] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiClassify, setAiClassify] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/library/v2/subfolders?category=${category}&mode=${mode}`);
        setAvailableSubs(data || []);
        if (!subfolder && data && data.length > 0) setSubfolder(data[0].name);
      } catch { setAvailableSubs([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, mode]);

  // Sections dispo dans le client sélectionné
  useEffect(() => {
    if (mode !== 'client' || !subfolder) { setAvailableSections([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/library/v2/sections?category=${category}&client=${encodeURIComponent(subfolder)}`);
        setAvailableSections(data?.sections || []);
      } catch { setAvailableSections([]); }
    })();
  }, [category, mode, subfolder]);

  const submit = async () => {
    if (!subfolder.trim()) {
      toast.error(mode === 'client' ? 'Choisis un client' : 'Choisis un sous-dossier');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim() || 'Sans titre',
        content,
        category,
        mode,
        subfolder: subfolder.trim(),
      };
      if (mode === 'client') payload.section = section.trim();
      const { data } = await api.post('/library/v2/notes', payload);
      onCreated(data);
    } catch (e) {
      toast.error('Erreur création');
    }
    setSaving(false);
  };

  return (
    <ModalShell onClose={onClose} title="Nouvelle note" testId="lib-new-note-modal">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FieldLabel>Titre</FieldLabel>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titre de la note"
          data-testid="lib-new-note-title"
          style={inputStyle}
          autoFocus
        />

        <FieldLabel>Catégorie</FieldLabel>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="lib-new-note-category" style={inputStyle}>
          {CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>

        <FieldLabel>Emplacement</FieldLabel>
        <div style={{ display: 'flex', gap: 8 }}>
          <ModeBtn active={mode === 'client'} onClick={() => { setMode('client'); setSubfolder(''); setSection(''); }} icon={<Users size={14} />} label="Client" testId="lib-new-note-mode-client" />
          <ModeBtn active={mode === 'interne'} onClick={() => { setMode('interne'); setSubfolder(''); setSection(''); }} icon={<Settings2 size={14} />} label="Interne" testId="lib-new-note-mode-interne" />
        </div>

        {mode === 'client' ? (
          <>
            <FieldLabel>Client</FieldLabel>
            <select value={subfolder} onChange={(e) => setSubfolder(e.target.value)} data-testid="lib-new-note-client" style={inputStyle}>
              <option value="">— Choisir un client —</option>
              {availableSubs.filter((s) => s.mode === 'client').map((s) => (
                <option key={s.name} value={s.name}>{s.name}{s.count > 0 ? ` (${s.count})` : ''}</option>
              ))}
            </select>
            <FieldLabel>Section (optionnel)</FieldLabel>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={availableSections.some((s) => s.name === section) ? section : ''} onChange={(e) => setSection(e.target.value)} data-testid="lib-new-note-section" style={{ ...inputStyle, flex: 1 }}>
                <option value="">— Racine du client —</option>
                {availableSections.map((s) => (
                  <option key={s.name} value={s.name}>{s.name} ({s.count})</option>
                ))}
              </select>
              <input
                placeholder="ou nouvelle section…"
                value={availableSections.some((s) => s.name === section) ? '' : section}
                onChange={(e) => setSection(e.target.value)}
                data-testid="lib-new-note-section-input"
                style={{ ...inputStyle, flex: 1 }}
              />
            </div>
          </>
        ) : (
          <>
            <FieldLabel>Sous-dossier</FieldLabel>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={availableSubs.some((s) => s.name === subfolder) ? subfolder : ''} onChange={(e) => setSubfolder(e.target.value)} data-testid="lib-new-note-subfolder" style={{ ...inputStyle, flex: 1 }}>
                <option value="">— Choisir un sous-dossier —</option>
                {availableSubs.filter((s) => s.mode === 'interne').map((s) => (
                  <option key={s.name} value={s.name}>{s.name} ({s.count})</option>
                ))}
              </select>
              <input
                placeholder="ou nouveau nom…"
                value={availableSubs.some((s) => s.name === subfolder) ? '' : subfolder}
                onChange={(e) => setSubfolder(e.target.value)}
                data-testid="lib-new-note-subfolder-input"
                style={{ ...inputStyle, flex: 1 }}
              />
            </div>
          </>
        )}

        <FieldLabel>Contenu</FieldLabel>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Contenu de la note…"
          data-testid="lib-new-note-content"
          rows={7}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'Sora', lineHeight: 1.5 }}
        />

        {/* AI classify placeholder — phase 2 (Haiku) */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', borderRadius: 11,
          background: 'rgba(91,190,252,0.06)', border: `1px dashed ${BORDER_S}`, cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={aiClassify}
            onChange={(e) => setAiClassify(e.target.checked)}
            data-testid="lib-new-note-ai-toggle"
            style={{ accentColor: BLUE }}
          />
          <Sparkles size={14} color={BLUE_LIGHT} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: T1 }}>Laisser l'IA ranger cette note</div>
            <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 2 }}>
              Bientôt : Claude Haiku analysera le contenu et suggérera catégorie + emplacement
            </div>
          </div>
        </label>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onClose} data-testid="lib-new-note-cancel" style={btnSecondary}>Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="lib-new-note-submit" style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Création…' : 'Créer'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── New subfolder modal (mode=interne only) ─────────────────────────────────
function NewSubfolderModal({ category, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      await api.post('/library/v2/subfolders', { category, name: name.trim() });
      onCreated();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur';
      toast.error(msg);
    }
    setSaving(false);
  };
  return (
    <ModalShell onClose={onClose} title="Nouveau sous-dossier" testId="lib-new-subfolder-modal">
      <FieldLabel>Nom</FieldLabel>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Ex: Onboarding, Contrats, Réunions…"
        data-testid="lib-new-subfolder-input"
        style={inputStyle}
        autoFocus
        onKeyDown={(e) => e.key === 'Enter' && submit()}
      />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose} style={btnSecondary}>Annuler</button>
        <button onClick={submit} disabled={saving} data-testid="lib-new-subfolder-submit" style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>Créer</button>
      </div>
    </ModalShell>
  );
}

// ─── Note editor modal ───────────────────────────────────────────────────────
function NoteEditorModal({ note, onClose, onSaved, onDeleted }) {
  const [title, setTitle] = useState(note.title || '');
  const [content, setContent] = useState(note.content || '');
  const [saving, setSaving] = useState(false);
  const initialLoad = useRef(true);

  // Mark viewed
  useEffect(() => {
    api.post(`/library/v2/notes/${note.id}/view`).catch(() => {});
  }, [note.id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/library/v2/notes/${note.id}`, { title: title.trim() || 'Sans titre', content });
      onSaved(data);
      toast.success('Enregistré');
      onClose();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const remove = async () => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      await api.delete(`/library/v2/notes/${note.id}`);
      onDeleted(note.id);
    } catch { toast.error('Erreur suppression'); }
  };

  // Iter 62ee — Créer un Google Doc lié à cette note (via drive.py existant)
  const createGoogleDoc = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/drive/docs/create-from-note/${note.id}`);
      if (data?.web_view_link) {
        toast.success('Google Doc créé');
        window.open(data.web_view_link, '_blank');
        onSaved({ ...note, docs_doc_id: data.doc_id, docs_web_view_link: data.web_view_link });
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur création Google Doc — vérifie que Drive est connecté';
      toast.error(msg);
    }
    setSaving(false);
  };

  return (
    <ModalShell onClose={onClose} title={note.title || 'Note'} testId="lib-note-editor-modal" wide>
      <FieldLabel>Titre</FieldLabel>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        data-testid="lib-editor-title"
        style={inputStyle}
      />
      <div style={{ display: 'flex', gap: 12, fontFamily: 'Sora', fontSize: 11, color: T3, margin: '12px 0' }}>
        <span>{catLabel(note.lib_category)}</span>
        <span>·</span>
        <span>{note.lib_mode === 'client' ? 'Client' : 'Interne'}</span>
        <span>·</span>
        <span>{note.lib_subfolder}</span>
      </div>
      <FieldLabel>Contenu</FieldLabel>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={18}
        data-testid="lib-editor-content"
        style={{ ...inputStyle, resize: 'vertical', fontFamily: 'Sora', lineHeight: 1.55 }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontFamily: 'Sora', fontSize: 11, color: T3, marginTop: 14 }}>
        <div>Créée le {formatDate(note.created_at)}</div>
        <div>Dernière modification le {formatDate(note.updated_at)}</div>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 20, flexWrap: 'wrap' }}>
        <button onClick={remove} data-testid="lib-editor-delete" style={{ ...btnSecondary, color: '#F87171', borderColor: 'rgba(248,113,113,0.30)' }}>
          <Trash2 size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Supprimer
        </button>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {note.docs_web_view_link ? (
            <a
              href={note.docs_web_view_link}
              target="_blank" rel="noreferrer"
              data-testid="lib-editor-open-doc"
              style={{ ...btnSecondary, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, color: BLUE_LIGHT, borderColor: 'rgba(91,190,252,0.30)' }}
            >
              <FileText size={13} /> Ouvrir Google Doc
            </a>
          ) : (
            <button onClick={createGoogleDoc} disabled={saving} data-testid="lib-editor-create-doc" style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6, color: BLUE_LIGHT, borderColor: 'rgba(91,190,252,0.30)', opacity: saving ? 0.6 : 1 }}>
              <Sparkles size={13} /> Créer Google Doc
            </button>
          )}
          <button onClick={onClose} style={btnSecondary}>Fermer</button>
          <button onClick={save} disabled={saving} data-testid="lib-editor-save" style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── DriveCategoryView : navigation dans le dossier Drive mappé ──────────────
function DriveCategoryView({ category, onBack, onOpenMapping, viewMode = 'notes' }) {
  const cat = CATEGORIES.find((c) => c.id === category);
  const [state, setState] = useState({ loading: true, mapped: true, folders: [], docs: [], other: [], root_folder_name: '' });
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(null); // {done, total, current}
  const [displayLimit, setDisplayLimit] = useState(100); // pagination for perf
  const [scanProgress, setScanProgress] = useState(0); // 0-100 for progress bar
  const isFilesMode = viewMode === 'files';

  useEffect(() => {
    let alive = true;
    let progressTimer = null;
    (async () => {
      setState((s) => ({ ...s, loading: true }));
      setScanProgress(6);
      // Simulate a smooth progress bar that eases toward 92% over ~8s while
      // the real deep-scan runs. Snaps to 100 when the HTTP call resolves.
      const startedAt = Date.now();
      progressTimer = setInterval(() => {
        if (!alive) return;
        const elapsed = (Date.now() - startedAt) / 1000;
        // Ease-out: fast at start, slow near the ceiling
        const target = Math.min(92, 6 + (1 - Math.exp(-elapsed / 3.5)) * 86);
        setScanProgress((p) => (target > p ? target : p));
      }, 120);
      try {
        const { data } = await api.get(`/library/v2/drive/category/${category}/deep-scan`);
        if (!alive) return;
        setScanProgress(100);
        setState({
          loading: false, mapped: true,
          folders: data.folders || [],
          docs: data.docs || [],
          other: data.files || [],
          root_folder_name: data.root_folder_name,
          counts: data.counts,
        });
      } catch (e) {
        if (!alive) return;
        setScanProgress(100);
        const status = e?.response?.status;
        if (status === 404) {
          setState({ loading: false, mapped: false, disconnected: false, folders: [], docs: [], other: [] });
        } else if (status === 401) {
          setState({ loading: false, mapped: false, disconnected: true, folders: [], docs: [], other: [] });
        } else {
          toast.error(`Erreur: ${e?.response?.data?.detail || e.message}`);
          setState({ loading: false, mapped: true, disconnected: false, folders: [], docs: [], other: [] });
        }
      }
    })();
    return () => {
      alive = false;
      if (progressTimer) clearInterval(progressTimer);
    };
  }, [category, reloadKey]);

  const openDoc = (d) => {
    // Track for the "did you just use a doc?" prompt
    try {
      sessionStorage.setItem('lib_recent_doc', JSON.stringify({
        id: d.id, name: d.name, webViewLink: d.webViewLink,
        category, opened_at: Date.now(),
      }));
    } catch {}
    window.open(d.webViewLink, '_blank');
  };

  const createDoc = async (title) => {
    setCreating(true);
    try {
      const { data } = await api.post(`/library/v2/drive/category/${category}/create-doc`, {
        title: title || 'Sans titre',
      });
      toast.success(`Doc "${data.name}" créé dans Drive`);
      try {
        sessionStorage.setItem('lib_recent_doc', JSON.stringify({
          id: data.id, name: data.name, webViewLink: data.web_view_link,
          category, opened_at: Date.now(),
        }));
      } catch {}
      window.open(data.web_view_link, '_blank');
      setShowCreateModal(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(`Erreur création: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading({ done: 0, total: files.length, current: files[0]?.name, percent: 0 });
    let done = 0;
    for (const f of files) {
      setUploading({ done, total: files.length, current: f.name, percent: 0 });
      const form = new FormData();
      form.append('file', f);
      try {
        await api.post(`/library/v2/drive/category/${category}/upload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (evt.total) {
              const pct = Math.round((evt.loaded / evt.total) * 100);
              setUploading((u) => u ? { ...u, current: f.name, percent: pct } : u);
            }
          },
        });
        done += 1;
      } catch (e) {
        toast.error(`Erreur upload "${f.name}": ${e?.response?.data?.detail || e.message}`);
      }
    }
    setUploading(null);
    if (done > 0) toast.success(`${done}/${files.length} fichier(s) uploadé(s)`);
    setReloadKey((k) => k + 1);
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  return (
    <div
      style={{ paddingTop: 10, position: 'relative' }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Overlay drop zone */}
      {dragOver && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 150,
            background: 'rgba(62, 123, 250, 0.18)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: `3px dashed ${BLUE_LIGHT}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div style={{ textAlign: 'center', color: '#F8FBFF' }}>
            <Upload size={48} strokeWidth={2.2} style={{ marginBottom: 12 }} />
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 22 }}>Lâche ici pour uploader vers {cat?.label || category}</div>
            <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 13, opacity: 0.85, marginTop: 6 }}>
              Les fichiers seront envoyés directement dans ton Drive
            </div>
          </div>
        </div>
      )}
      {uploading && (
        <div
          style={{
            position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)',
            zIndex: 90, padding: '14px 20px', borderRadius: 14,
            background: 'linear-gradient(180deg, #17223F 0%, #0F1730 100%)',
            border: `1px solid rgba(91,190,252,0.28)`,
            boxShadow: '0 20px 50px -12px rgba(3,7,20,0.8)',
            fontFamily: 'Sora', fontSize: 13, color: T1,
            minWidth: 320, maxWidth: 440,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Upload size={16} color={BLUE_LIGHT} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>Upload · {uploading.done}/{uploading.total}</div>
              <div style={{ fontSize: 11, color: T3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{uploading.current}</div>
            </div>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 14, color: BLUE_LIGHT, minWidth: 40, textAlign: 'right' }}>
              {uploading.percent || 0}%
            </div>
          </div>
          {/* Bar */}
          <div style={{
            height: 6, borderRadius: 3, overflow: 'hidden',
            background: 'rgba(91,190,252,0.14)',
          }}>
            <div style={{
              width: `${uploading.percent || 0}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
              transition: 'width 0.25s ease-out',
              boxShadow: `0 0 12px ${BLUE_LIGHT}80`,
            }} />
          </div>
        </div>
      )}
      {/* Header : back + titre + counters + refresh — pas de bouton "+Nouveau doc" ici, il est dans la section Docs plus bas pour éviter la collision avec la floating bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, paddingRight: 320 }}>
        <button
          onClick={onBack}
          data-testid="lib-back-hub-btn"
          className="glass-pill glass-apple-hover"
          style={{
            padding: '10px 14px', borderRadius: 12, cursor: 'pointer',
            color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        ><ArrowLeft size={14} /> Retour</button>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 22, color: T1 }}>
            {cat?.label || category}
            {isFilesMode && <span style={{ fontSize: 14, fontWeight: 500, color: T3, marginLeft: 10 }}>· Fichiers</span>}
          </div>
          {state.counts && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {!isFilesMode && <TileStatChip color="#4285F4" icon={<FileText size={11} strokeWidth={2.4} />} value={state.counts.docs} label="doc" />}
              <TileStatChip color={BLUE_LIGHT} icon={<Folder size={11} strokeWidth={2.4} />} value={state.counts.folders} label="dossier" />
              {isFilesMode && <TileStatChip color="#38BDF8" icon={<Sparkles size={11} strokeWidth={2.4} />} value={state.counts.files} label="fichier" />}
            </div>
          )}
        </div>
        {state.mapped && (
          <>
          <label
            data-testid="lib-upload-btn"
            className="glass-pill glass-apple-hover"
            title="Uploader un fichier (ou glisse-dépose n'importe où)"
            style={{
              padding: '10px 12px', borderRadius: 12, cursor: 'pointer',
              color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <Upload size={13} />
            <input
              type="file"
              multiple
              onChange={(e) => uploadFiles(e.target.files)}
              style={{ display: 'none' }}
            />
          </label>
          <button
            onClick={async () => {
              if (!confirm(`Générer les résumés IA pour tous les docs de ${cat?.label} (max 50) ? Peut prendre 30-60 sec.`)) return;
              toast.info('Génération des résumés IA en cours…');
              try {
                const { data } = await api.post(`/library/v2/ai/summaries/batch/${category}?max_docs=50`);
                toast.success(`${data.processed} résumés générés · ${data.skipped_cached} déjà cached · ${data.failed} échecs`);
                setReloadKey((k) => k + 1);
              } catch (e) {
                toast.error(`Erreur: ${e?.response?.data?.detail || e.message}`);
              }
            }}
            data-testid="lib-batch-summarize-btn"
            className="glass-pill glass-apple-hover"
            title="Générer les résumés IA de tous les docs (Claude Haiku)"
            style={{
              padding: '10px 12px', borderRadius: 12, cursor: 'pointer',
              color: BLUE_LIGHT, fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          ><Sparkles size={13} /></button>
          <button
            onClick={async () => {
              try {
                await api.post(`/library/v2/drive/category/${category}/refresh`);
              } catch {}
              setReloadKey((k) => k + 1);
            }}
            data-testid="lib-refresh-btn"
            className="glass-pill glass-apple-hover"
            title="Rafraîchir depuis Drive (invalide le cache serveur)"
            style={{
              padding: '10px 12px', borderRadius: 12, cursor: 'pointer',
              color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          ><RefreshCw size={13} /></button>
          </>
        )}
      </div>

      {/* Content */}
      {state.loading && (
        <div
          data-testid="lib-drive-scan-progress"
          style={{
            padding: '40px 32px 44px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18,
            fontFamily: 'Sora',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: T2, fontSize: 13, fontWeight: 700 }}>
            <RefreshCw size={14} style={{ animation: 'spin 1.4s linear infinite' }} />
            <span>Scan Drive · {cat?.label || category}</span>
            <span style={{ color: T3, fontWeight: 500 }}>·</span>
            <span style={{ color: BLUE_LIGHT, fontVariantNumeric: 'tabular-nums', minWidth: 42 }}>
              {Math.round(scanProgress)}%
            </span>
          </div>
          <div
            style={{
              width: 'min(520px, 80%)',
              height: 6,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.06)',
              overflow: 'hidden',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
            }}
          >
            <div
              style={{
                width: `${scanProgress}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${BLUE_LIGHT} 0%, #7DB6FF 100%)`,
                borderRadius: 999,
                transition: 'width 220ms cubic-bezier(0.22, 0.61, 0.36, 1)',
                boxShadow: `0 0 12px ${BLUE_LIGHT}66`,
              }}
            />
          </div>
          <div style={{ color: T3, fontSize: 11, letterSpacing: 0.3 }}>
            Lecture récursive de l'arbre Drive · patiente quelques secondes
          </div>
          <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
        </div>
      )}
      {!state.loading && !state.mapped && (
        <div style={{
          padding: '60px 32px', textAlign: 'center',
          background: SURFACE, borderRadius: 20, border: `1px solid ${BORDER}`,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
            background: state.disconnected ? 'rgba(255,184,107,0.14)' : 'rgba(62,123,250,0.14)',
            border: `1px solid ${state.disconnected ? 'rgba(255,184,107,0.30)' : BORDER_S}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Link2 size={28} color={state.disconnected ? '#FFB86B' : BLUE_LIGHT} /></div>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 20, color: T1, marginBottom: 8 }}>
            {state.disconnected ? 'Google Drive déconnecté' : 'Catégorie non liée à un dossier Drive'}
          </div>
          <div style={{ fontFamily: 'Sora', fontSize: 13, color: T3, marginBottom: 20, maxWidth: 480, margin: '0 auto 20px' }}>
            {state.disconnected
              ? 'Ton token OAuth a expiré. Reconnecte Google Drive en haut à droite et tout revient — le mapping et tes docs sont bien sauvegardés.'
              : 'Pour voir tes Google Docs, mappe cette catégorie à un dossier de ton Drive.'}
          </div>
          {!state.disconnected && (
            <button
              onClick={onOpenMapping}
              data-testid="lib-map-now-btn"
              className="glass-apple-hover"
              style={{
                padding: '11px 20px', borderRadius: 14, cursor: 'pointer',
                background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
                color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
              }}
            >Configurer le mapping Drive</button>
          )}
        </div>
      )}
      {!state.loading && state.mapped && (
        <>
          {state.folders.length === 0 && state.docs.length === 0 && state.other.length === 0 && (
            <div style={{ padding: 60, textAlign: 'center', color: T3, fontFamily: 'Sora', fontSize: 14, background: SURFACE, borderRadius: 20, border: `1px solid ${BORDER}` }}>
              {isFilesMode ? 'Aucun fichier ici.' : 'Dossier vide. Crée un nouveau doc avec le bouton ci-dessus.'}
            </div>
          )}
          {state.folders.length > 0 && (() => {
            // Afficher uniquement les dossiers RACINE (path === []).
            // Les sous-dossiers sont accessibles en cliquant sur un dossier
            // (ouvre Google Drive) ou visibles dans les compteurs "N dossier".
            const rootFolders = state.folders.filter((f) => !(f.path || []).length);
            const displayed = rootFolders.slice(0, displayLimit);
            if (rootFolders.length === 0) return null;
            return (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                Dossiers ({rootFolders.length}{rootFolders.length > displayLimit ? ` — affichés : ${displayLimit}` : ''})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                {displayed.map((f) => {
                  // Compute stats from deep-scan: count docs/files whose path starts with this folder's full path
                  const fullPath = [...(f.path || []), f.name];
                  const pathPrefix = fullPath.join('/');
                  const docsInside = state.docs.filter((d) => (d.path || []).join('/').startsWith(pathPrefix)).length;
                  const filesInside = state.other.filter((d) => (d.path || []).join('/').startsWith(pathPrefix)).length;
                  const foldersInside = state.folders.filter((x) => (x.path || []).join('/').startsWith(pathPrefix) && x.id !== f.id).length;
                  const relevantCount = isFilesMode ? filesInside : docsInside;
                  const hasContent = relevantCount > 0;
                  const pathLabel = (f.path && f.path.length) ? f.path.join(' / ') : (state.root_folder_name || '');
                  return (
                    <button
                      key={f.id}
                      onClick={() => {
                        pushRecentFolder({
                          id: f.id,
                          name: f.name,
                          webViewLink: f.webViewLink,
                          category, // parent category id
                          category_label: cat?.label,
                          path: f.path || [],
                        });
                        window.open(f.webViewLink, '_blank');
                      }}
                      data-testid={`lib-drive-folder-${f.id}`}
                      className="glass-apple glass-apple-hover"
                      title="Ouvrir dans Google Drive"
                      style={{
                        padding: '14px 14px', borderRadius: 14, cursor: 'pointer', color: T1,
                        display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                        opacity: hasContent || foldersInside > 0 ? 1 : 0.55,
                        borderLeft: hasContent ? `3px solid ${isFilesMode ? '#38BDF8' : '#4285F4'}` : '3px solid transparent',
                      }}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: 9,
                        background: hasContent
                          ? `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`
                          : 'rgba(62,123,250,0.22)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        boxShadow: hasContent ? '0 8px 20px -6px rgba(62,123,250,0.55)' : 'none',
                      }}><Folder size={16} color={hasContent ? '#F8FBFF' : T2} strokeWidth={2.4} /></div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                        <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {pathLabel}
                        </div>
                        <div style={{ display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap' }}>
                          {!isFilesMode && docsInside > 0 && <FolderStatMini color="#4285F4" value={docsInside} label="doc" />}
                          {isFilesMode && filesInside > 0 && <FolderStatMini color="#38BDF8" value={filesInside} label="fichier" />}
                          {foldersInside > 0 && <FolderStatMini color={BLUE_LIGHT} value={foldersInside} label="dossier" />}
                          {((isFilesMode && filesInside === 0 && foldersInside === 0) || (!isFilesMode && docsInside === 0 && foldersInside === 0)) && (
                            <span style={{ fontFamily: 'Sora', fontSize: 10.5, color: T3, fontStyle: 'italic' }}>vide</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            );
          })()}
          {!isFilesMode && state.docs.length > 0 && (
            <div>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                Google Docs ({state.docs.length}{state.docs.length > displayLimit ? ` — affichés : ${displayLimit}` : ''})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
                <NewDocTile onClick={() => setShowCreateModal(true)} />
                {state.docs.slice(0, displayLimit).map((d) => (
                  <DriveDocCard
                    key={d.id}
                    doc={d}
                    onOpen={() => openDoc(d)}
                    onDelete={() => setConfirmDelete(d)}
                  />
                ))}
              </div>
              {state.docs.length > displayLimit && (
                <button
                  onClick={() => setDisplayLimit((l) => l + 100)}
                  data-testid="lib-load-more-docs"
                  className="glass-pill glass-apple-hover"
                  style={{
                    marginTop: 14, padding: '10px 18px', borderRadius: 12, cursor: 'pointer',
                    color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                    display: 'block', margin: '14px auto 0',
                  }}
                >Charger 100 de plus ({state.docs.length - displayLimit} restants)</button>
              )}
            </div>
          )}
          {!isFilesMode && state.docs.length === 0 && state.folders.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                Google Docs (0)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
                <NewDocTile onClick={() => setShowCreateModal(true)} />
              </div>
            </div>
          )}
          {isFilesMode && state.other.length > 0 && (
            <div>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                Fichiers ({state.other.length}{state.other.length > displayLimit ? ` — affichés : ${displayLimit}` : ''})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                {state.other.slice(0, displayLimit).map((f) => (
                  <DriveFileCard
                    key={f.id}
                    file={f}
                    onOpen={() => setPreviewFile(f)}
                    onDelete={() => setConfirmDelete(f)}
                  />
                ))}
              </div>
              {state.other.length > displayLimit && (
                <button
                  onClick={() => setDisplayLimit((l) => l + 100)}
                  data-testid="lib-load-more-files"
                  className="glass-pill glass-apple-hover"
                  style={{
                    marginTop: 14, padding: '10px 18px', borderRadius: 12, cursor: 'pointer',
                    color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                    display: 'block', margin: '14px auto 0',
                  }}
                >Charger 100 de plus ({state.other.length - displayLimit} restants)</button>
              )}
            </div>
          )}
        </>
      )}
      {showCreateModal && !isFilesMode && (
        <QuickCreateDocModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createDoc}
          creating={creating}
          categoryLabel={cat?.label || category}
        />
      )}
      {confirmDelete && (
        <ConfirmTrashModal
          doc={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={async () => {
            try {
              await api.post(`/library/v2/drive/docs/${confirmDelete.id}/trash`, { also_delete_from_drive: true });
              toast.success(`"${confirmDelete.name}" envoyé dans la corbeille Drive`);
              setConfirmDelete(null);
              setReloadKey((k) => k + 1);
            } catch (e) {
              toast.error(`Erreur: ${e?.response?.data?.detail || e.message}`);
            }
          }}
        />
      )}
      {previewFile && (
        <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
    </div>
  );
}

function FolderStatMini({ color, value, label }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '2px 6px', borderRadius: 6,
      background: `${color}18`, border: `1px solid ${color}30`,
      fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: color,
    }}>{value} {label}{value > 1 ? 's' : ''}</span>
  );
}

// ─── FilePreviewModal : ouvre une vidéo / image / PDF en overlay ─────────────
function FilePreviewModal({ file, onClose }) {
  const mime = file.mimeType || '';
  const isImage = mime.startsWith('image/');
  const isVideo = mime.startsWith('video/');
  const isPdf = mime === 'application/pdf';
  // Drive preview URL works well in iframe for most types
  const previewUrl = `https://drive.google.com/file/d/${file.id}/preview`;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Lock scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      data-testid="lib-file-preview-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(3, 7, 20, 0.92)',
        backdropFilter: 'blur(20px) saturate(140%)',
        WebkitBackdropFilter: 'blur(20px) saturate(140%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(180deg, #17223F 0%, #0F1730 100%)',
          border: '1px solid rgba(91,190,252,0.24)',
          borderRadius: 20, overflow: 'hidden',
          width: '100%', maxWidth: 1200, maxHeight: '92vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 40px 90px -20px rgba(3,7,20,0.85), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid rgba(91,190,252,0.14)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: isVideo ? '#8B5CF620' : isImage ? '#38BDF820' : isPdf ? '#EA433520' : '#38BDF820',
            border: `1px solid ${isVideo ? '#8B5CF640' : isImage ? '#38BDF840' : isPdf ? '#EA433540' : '#38BDF840'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <FileText size={14} color={isVideo ? '#8B5CF6' : isImage ? '#38BDF8' : isPdf ? '#EA4335' : '#38BDF8'} />
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 14, color: T1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</div>
            <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11, color: T3, marginTop: 2 }}>
              📁 {file.path && file.path.length ? file.path.join(' / ') : 'Racine'}
            </div>
          </div>
          <button
            onClick={() => window.open(file.webViewLink, '_blank')}
            data-testid="lib-file-preview-open-drive"
            className="glass-pill glass-apple-hover"
            style={{
              padding: '8px 12px', borderRadius: 10, cursor: 'pointer',
              color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          ><ExternalLink size={12} /> Ouvrir dans Drive</button>
          <button
            onClick={onClose}
            data-testid="lib-file-preview-close"
            style={{
              padding: 8, borderRadius: 10, cursor: 'pointer',
              background: 'transparent', border: '1px solid rgba(255,255,255,0.10)',
              color: T2,
            }}
          ><X size={14} /></button>
        </div>
        {/* Body */}
        <div style={{ flex: 1, background: '#000', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <iframe
            src={previewUrl}
            title={file.name}
            style={{ width: '100%', height: '100%', minHeight: 500, border: 'none' }}
            allow="autoplay; encrypted-media; fullscreen"
          />
        </div>
      </div>
    </div>
  );
}

function NewDocTile({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="lib-new-doc-tile"
      className="glass-apple-hover"
      style={{
        padding: 14, borderRadius: 14, cursor: 'pointer', color: '#F8FBFF', textAlign: 'left',
        display: 'flex', alignItems: 'center', gap: 12,
        background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
        border: '1px solid rgba(255,255,255,0.20)',
        boxShadow: '0 12px 28px -10px rgba(62,123,250,0.55), inset 0 1px 0 rgba(255,255,255,0.28)',
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 9,
        background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.18)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}><Plus size={17} strokeWidth={2.8} /></div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5 }}>Nouveau doc</div>
        <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11, opacity: 0.85, marginTop: 2 }}>
          Crée un Google Doc directement dans cette catégorie
        </div>
      </div>
    </button>
  );
}

function DriveDocCard({ doc, onOpen, onDelete }) {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const pathLabel = doc.path && doc.path.length ? doc.path.join(' / ') : (doc.path_str || 'Racine');
  const relativeDate = formatRelativeDate(doc.modifiedTime);

  useEffect(() => {
    let alive = true;
    // Lazy summary: fetch cached version only (no LLM call) — the API will
    // hit LLM on first call, then cache. To keep it cheap, we only fetch
    // when the user has manually clicked "Résumer" (unless already cached).
    (async () => {
      try {
        // Check for cached summary only, without forcing new gen
        const { data } = await api.get(`/library/v2/ai/summary/${doc.id}`);
        if (!alive) return;
        if (data.cached) setSummary(data.summary);
      } catch {}
    })();
    return () => { alive = false; };
  }, [doc.id]);

  const generateSummary = async (e) => {
    e.stopPropagation();
    setSummaryLoading(true);
    try {
      const { data } = await api.get(`/library/v2/ai/summary/${doc.id}?force=false`);
      setSummary(data.summary);
    } catch (err) {
      toast.error(`Erreur résumé: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <div
      data-testid={`lib-drive-doc-${doc.id}`}
      className="glass-apple glass-apple-hover"
      style={{
        padding: 14, borderRadius: 14, color: T1, textAlign: 'left',
        display: 'flex', flexDirection: 'column', gap: 10,
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onOpen}
          style={{
            flex: 1, background: 'transparent', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 12, color: 'inherit', padding: 0, textAlign: 'left',
          }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: 'rgba(66, 133, 244, 0.16)', border: '1px solid rgba(66, 133, 244, 0.28)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}><FileText size={16} color="#4285F4" /></div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.name}</div>
            <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              📁 {pathLabel}
            </div>
          </div>
          <ExternalLink size={13} color={T3} />
        </button>
        <button
          onClick={onDelete}
          data-testid={`lib-doc-trash-${doc.id}`}
          title="Supprimer (envoyer dans la corbeille Drive)"
          style={{
            padding: 7, borderRadius: 8, cursor: 'pointer',
            background: 'transparent', border: '1px solid rgba(255,122,122,0.20)',
            color: '#FF7A7A', flexShrink: 0,
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,122,122,0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        ><Trash2 size={12} /></button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, fontStyle: 'italic', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {summary
            ? <><span style={{ fontStyle: 'normal', color: BLUE_LIGHT, fontWeight: 700 }}>✨</span> {summary}</>
            : <>Modifié {relativeDate}</>}
        </div>
        {!summary && (
          <button
            onClick={generateSummary}
            disabled={summaryLoading}
            data-testid={`lib-doc-summarize-${doc.id}`}
            title="Générer un résumé IA (Claude Haiku)"
            style={{
              padding: '4px 8px', borderRadius: 7, cursor: summaryLoading ? 'wait' : 'pointer',
              background: 'rgba(91,190,252,0.10)', border: '1px solid rgba(91,190,252,0.28)',
              color: BLUE_LIGHT, fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
              display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
            }}
          >
            <Sparkles size={10} strokeWidth={2.4} />
            {summaryLoading ? '…' : 'Résumer'}
          </button>
        )}
      </div>
    </div>
  );
}

function DriveFileCard({ file, onOpen, onDelete }) {
  const mime = file.mimeType || '';
  const isVideo = mime.startsWith('video/');
  const isImage = mime.startsWith('image/');
  const isPdf = mime === 'application/pdf';
  const color = isVideo ? '#8B5CF6' : isImage ? '#38BDF8' : isPdf ? '#EA4335' : '#38BDF8';
  const label = isVideo ? 'Vidéo' : isImage ? 'Image' : isPdf ? 'PDF' : (mime.split('/').pop() || 'fichier');
  return (
    <div
      data-testid={`lib-drive-file-${file.id}`}
      className="glass-apple glass-apple-hover"
      style={{
        padding: 14, borderRadius: 14, color: T1, textAlign: 'left',
        display: 'flex', alignItems: 'center', gap: 12,
      }}
    >
      <button
        onClick={onOpen}
        style={{
          flex: 1, background: 'transparent', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12, color: 'inherit', padding: 0, textAlign: 'left',
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: `${color}20`, border: `1px solid ${color}40`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          fontFamily: 'Sora', fontWeight: 800, fontSize: 9, color: color,
        }}>{label.slice(0, 3).toUpperCase()}</div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</div>
          <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            📁 {file.path && file.path.length ? file.path.join(' / ') : (file.path_str || 'Racine')}
          </div>
          <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 1, fontStyle: 'italic' }}>
            {label} · {formatRelativeDate(file.modifiedTime)}
          </div>
        </div>
        <ExternalLink size={13} color={T3} />
      </button>
      <button
        onClick={onDelete}
        data-testid={`lib-file-trash-${file.id}`}
        title="Supprimer (envoyer dans la corbeille Drive)"
        style={{
          padding: 7, borderRadius: 8, cursor: 'pointer',
          background: 'transparent', border: '1px solid rgba(255,122,122,0.20)',
          color: '#FF7A7A', flexShrink: 0,
        }}
      ><Trash2 size={12} /></button>
    </div>
  );
}

function ConfirmTrashModal({ doc, onClose, onConfirm }) {
  return (
    <ModalShell onClose={onClose} title="Envoyer dans la corbeille ?" testId="lib-confirm-trash">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontFamily: 'Sora', fontSize: 13, color: T2, lineHeight: 1.5 }}>
          « <strong style={{ color: T1 }}>{doc.name}</strong> » sera envoyé dans la corbeille de ton Google Drive.
          <br />
          <span style={{ fontSize: 12, color: T3 }}>
            Tu pourras le récupérer pendant 30 jours depuis Drive → Corbeille.
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            onClick={onClose}
            data-testid="lib-confirm-trash-cancel"
            className="glass-pill glass-apple-hover"
            style={{ padding: '10px 18px', borderRadius: 12, cursor: 'pointer', color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12 }}
          >Annuler</button>
          <button
            onClick={onConfirm}
            data-testid="lib-confirm-trash-confirm"
            style={{
              padding: '10px 20px', borderRadius: 12, cursor: 'pointer',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(239,68,68,0.75) 60%, #EF4444 100%)',
              color: '#FFF', border: '1px solid rgba(255,255,255,0.18)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
            }}
          >Envoyer dans la corbeille</button>
        </div>
      </div>
    </ModalShell>
  );
}

function QuickCreateDocModal({ onClose, onCreate, creating, categoryLabel }) {
  const [title, setTitle] = useState('');
  return (
    <ModalShell onClose={onClose} title={`Nouveau doc · ${categoryLabel}`} testId="lib-new-doc-modal">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <FieldLabel>Titre du document</FieldLabel>
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && title.trim()) onCreate(title.trim()); }}
            placeholder="ex : Compte-rendu réunion Augustin"
            data-testid="lib-new-doc-title-input"
            className="glass-apple"
            style={{
              width: '100%', padding: '13px 16px', borderRadius: 12,
              color: T1, outline: 'none',
              fontFamily: 'Sora', fontWeight: 500, fontSize: 14,
            }}
          />
        </div>
        <div style={{ fontFamily: 'Sora', fontSize: 12, color: T3 }}>
          Le doc sera créé directement dans le dossier Drive lié à cette catégorie et ouvert dans un nouvel onglet.
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            onClick={onClose}
            data-testid="lib-new-doc-cancel"
            className="glass-pill glass-apple-hover"
            style={{ padding: '10px 18px', borderRadius: 12, cursor: 'pointer', color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12 }}
          >Annuler</button>
          <button
            onClick={() => title.trim() && onCreate(title.trim())}
            disabled={!title.trim() || creating}
            data-testid="lib-new-doc-confirm"
            className="glass-apple-hover"
            style={{
              padding: '10px 20px', borderRadius: 12, cursor: title.trim() && !creating ? 'pointer' : 'not-allowed',
              background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
              color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
              opacity: title.trim() && !creating ? 1 : 0.5,
            }}
          >{creating ? 'Création…' : 'Créer et ouvrir'}</button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── AiNoteModal : "Nouvelle note IA" depuis la vue principale ───────────────
function AiNoteModal({ onClose, onCreated }) {
  const [mode, setMode] = useState(null); // null = choose, 'ai' | 'manual'
  const [step, setStep] = useState('input'); // 'input' | 'suggestion' | 'creating'
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualCat, setManualCat] = useState('');
  const [manualSub, setManualSub] = useState('');

  const analyze = async () => {
    if (!title.trim()) { toast.error('Ajoute un titre'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/library/v2/ai/classify-note', {
        title: title.trim(),
        content: content.trim(),
      });
      setSuggestion(data);
      setStep('suggestion');
    } catch (e) {
      toast.error(`Erreur IA: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const doCreate = async ({ category, subfolder }) => {
    setLoading(true);
    try {
      const { data } = await api.post('/library/v2/ai/create-note', {
        title: title.trim(),
        content: content.trim(),
        category,
        subfolder_name: subfolder,
      });
      toast.success(`Note créée dans ${data.category_label} / ${data.subfolder}`);
      try {
        sessionStorage.setItem('lib_recent_doc', JSON.stringify({
          id: data.id, name: data.name, webViewLink: data.web_view_link,
          category: data.category, opened_at: Date.now(),
        }));
      } catch {}
      window.open(data.web_view_link, '_blank');
      onCreated();
    } catch (e) {
      toast.error(`Erreur création: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // STEP 0 : choose mode
  if (!mode) {
    return (
      <ModalShell onClose={onClose} title="Nouvelle note" testId="lib-note-mode-modal" wide>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontFamily: 'Sora', fontSize: 13, color: T2, marginBottom: 4 }}>
            Comment veux-tu ranger cette note ?
          </div>
          <button
            onClick={() => setMode('ai')}
            data-testid="lib-note-mode-ai"
            className="glass-apple glass-apple-hover"
            style={{
              padding: 20, borderRadius: 16, cursor: 'pointer', color: T1,
              display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
              borderLeft: `3px solid ${BLUE_LIGHT}`,
            }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              boxShadow: '0 10px 24px -8px rgba(62,123,250,0.6)',
            }}><Sparkles size={20} color="#F8FBFF" strokeWidth={2.4} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15 }}>L'IA range pour moi</div>
              <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 12, color: T3, marginTop: 3 }}>
                Claude Haiku analyse ta note et la place dans la bonne catégorie/sous-dossier
              </div>
            </div>
          </button>
          <button
            onClick={() => setMode('manual')}
            data-testid="lib-note-mode-manual"
            className="glass-apple glass-apple-hover"
            style={{
              padding: 20, borderRadius: 16, cursor: 'pointer', color: T1,
              display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
              borderLeft: `3px solid ${T2}`,
            }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'rgba(62,123,250,0.20)', border: `1px solid ${BORDER_S}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><Folder size={20} color={BLUE_LIGHT} strokeWidth={2.4} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15 }}>Je range moi-même</div>
              <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 12, color: T3, marginTop: 3 }}>
                Choisis la catégorie et le sous-dossier
              </div>
            </div>
          </button>
        </div>
      </ModalShell>
    );
  }

  // MANUAL mode
  if (mode === 'manual') {
    return (
      <ModalShell onClose={onClose} title="Nouvelle note · Rangement manuel" testId="lib-note-manual-modal" wide>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <FieldLabel>Titre</FieldLabel>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ex : Compte-rendu réunion Augustin"
              data-testid="lib-note-manual-title"
              className="glass-apple"
              style={{ width: '100%', padding: '13px 16px', borderRadius: 12, color: T1, outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 14 }}
            />
          </div>
          <div>
            <FieldLabel>Contenu (facultatif)</FieldLabel>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              className="glass-apple"
              style={{ width: '100%', padding: '13px 16px', borderRadius: 12, color: T1, outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 13, resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <FieldLabel>Catégorie</FieldLabel>
              <select
                value={manualCat}
                onChange={(e) => setManualCat(e.target.value)}
                data-testid="lib-note-manual-cat"
                className="glass-apple"
                style={{ width: '100%', padding: '11px 12px', borderRadius: 10, color: T1, fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5, outline: 'none', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}
              >
                <option value="" style={{ background: NAVY }}>— Choisir —</option>
                {CATEGORIES.map((c) => <option key={c.id} value={c.id} style={{ background: NAVY }}>{c.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel>Sous-dossier</FieldLabel>
              <input
                value={manualSub}
                onChange={(e) => setManualSub(e.target.value)}
                placeholder="ex : Zoldic ou Général"
                data-testid="lib-note-manual-sub"
                className="glass-apple"
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, color: T1, outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 13 }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
            <button
              onClick={() => setMode(null)}
              className="glass-pill glass-apple-hover"
              style={{ padding: '10px 18px', borderRadius: 12, cursor: 'pointer', color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12 }}
            >← Retour</button>
            <button
              onClick={() => doCreate({ category: manualCat, subfolder: manualSub.trim() || 'Général' })}
              disabled={!title.trim() || !manualCat || loading}
              data-testid="lib-note-manual-create"
              className="glass-apple-hover"
              style={{
                padding: '10px 22px', borderRadius: 12,
                cursor: title.trim() && manualCat && !loading ? 'pointer' : 'not-allowed',
                background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
                color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
                opacity: title.trim() && manualCat && !loading ? 1 : 0.5,
              }}
            >{loading ? 'Création…' : 'Créer et ouvrir'}</button>
          </div>
        </div>
      </ModalShell>
    );
  }

  // AI mode
  return (
    <ModalShell onClose={onClose} title="✨ Nouvelle note · Rangée par l'IA" testId="lib-ai-note-modal" wide>
      {step === 'input' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <FieldLabel>Titre</FieldLabel>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ex : Réunion Augustin — brief lancement"
              data-testid="lib-ai-note-title-input"
              className="glass-apple"
              style={{ width: '100%', padding: '13px 16px', borderRadius: 12, color: T1, outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 14 }}
            />
          </div>
          <div>
            <FieldLabel>Contenu (aide l'IA à mieux ranger)</FieldLabel>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Colle ici quelques notes / mots-clés…"
              data-testid="lib-ai-note-content-input"
              className="glass-apple"
              rows={6}
              style={{ width: '100%', padding: '13px 16px', borderRadius: 12, color: T1, outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 13, resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button
              onClick={() => setMode(null)}
              className="glass-pill glass-apple-hover"
              style={{ padding: '10px 18px', borderRadius: 12, cursor: 'pointer', color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12 }}
            >← Retour</button>
            <button
              onClick={analyze}
              disabled={!title.trim() || loading}
              data-testid="lib-ai-note-analyze"
              className="glass-apple-hover"
              style={{
                padding: '10px 22px', borderRadius: 12, cursor: title.trim() && !loading ? 'pointer' : 'not-allowed',
                background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
                color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
                opacity: title.trim() && !loading ? 1 : 0.5,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            ><Sparkles size={13} strokeWidth={2.4} />{loading ? 'Analyse…' : "Analyser avec l'IA"}</button>
          </div>
        </div>
      )}

      {step === 'suggestion' && suggestion && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ padding: 18, borderRadius: 14, background: 'rgba(91,190,252,0.10)', border: '1px solid rgba(91,190,252,0.28)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Sparkles size={16} color={BLUE_LIGHT} />
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: BLUE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Suggestion IA</div>
            </div>
            <div style={{ fontFamily: 'Sora', fontSize: 15, color: T1, marginBottom: 8 }}>
              Ranger dans
              <span style={{ color: '#F8FBFF', fontWeight: 800, marginLeft: 6 }}>{suggestion.category_label}</span>
              <span style={{ color: T3, margin: '0 8px' }}>›</span>
              <span style={{ color: '#F8FBFF', fontWeight: 800 }}>{suggestion.subfolder}</span>
              {suggestion.is_new_subfolder && (
                <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 6, fontSize: 10, background: 'rgba(126, 231, 135, 0.16)', border: '1px solid rgba(126, 231, 135, 0.30)', color: '#7EE787', fontWeight: 700 }}>NOUVEAU</span>
              )}
            </div>
            <div style={{ fontFamily: 'Sora', fontSize: 12, color: T3, fontStyle: 'italic' }}>{suggestion.reasoning}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setStep('input')}
              className="glass-pill glass-apple-hover"
              style={{ padding: '10px 18px', borderRadius: 12, cursor: 'pointer', color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12 }}
            >← Retour</button>
            <button
              onClick={() => doCreate({ category: suggestion.category, subfolder: suggestion.subfolder })}
              disabled={loading}
              data-testid="lib-ai-note-confirm"
              className="glass-apple-hover"
              style={{
                padding: '10px 22px', borderRadius: 12, cursor: loading ? 'wait' : 'pointer',
                background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
                color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
              }}
            >{loading ? 'Création…' : 'Confirmer et créer'}</button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ─── FilingPromptModal : "Tu viens d'utiliser ce doc, je le range ?" ─────────
function FilingPromptModal({ doc, onClose, onFiled }) {
  const [category, setCategory] = useState(doc.category || '');
  const [folders, setFolders] = useState([]);
  const [targetFolderId, setTargetFolderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (!category) return;
    setLoading(true);
    api.get(`/library/v2/drive/category/${category}/deep-scan`)
      .then((r) => {
        setFolders(r.data?.folders || []);
        // Preselect root
        setTargetFolderId(r.data?.root_folder_id || '');
      })
      .catch(() => setFolders([]))
      .finally(() => setLoading(false));
  }, [category]);

  const goBackToDoc = () => {
    window.open(doc.webViewLink, '_blank');
    onClose();
  };

  const fileNow = async () => {
    if (!targetFolderId) { toast.error('Choisis un dossier de destination'); return; }
    setMoving(true);
    try {
      await api.post(`/library/v2/drive/docs/${doc.id}/move`, { target_folder_id: targetFolderId });
      // Invalidate cache so refresh sees the new location
      if (category) {
        try { await api.post(`/library/v2/drive/category/${category}/refresh`); } catch {}
      }
      toast.success(`"${doc.name}" rangé ✅`);
      onFiled();
    } catch (e) {
      toast.error(`Erreur déplacement: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setMoving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} title="Tu viens d'utiliser un doc — je le range ?" testId="lib-filing-prompt-modal" wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{
          padding: 14, borderRadius: 12,
          background: 'rgba(66,133,244,0.10)', border: '1px solid rgba(66,133,244,0.28)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: 'rgba(66, 133, 244, 0.20)', border: '1px solid rgba(66, 133, 244, 0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}><FileText size={16} color="#4285F4" /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5, color: T1 }}>{doc.name}</div>
            <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11, color: T3, marginTop: 3 }}>Ouvert récemment</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <FieldLabel>Catégorie</FieldLabel>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="lib-filing-cat"
              className="glass-apple"
              style={{ width: '100%', padding: '11px 12px', borderRadius: 10, color: T1, fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5, outline: 'none', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}
            >
              <option value="" style={{ background: NAVY }}>— Choisir —</option>
              {CATEGORIES.map((c) => <option key={c.id} value={c.id} style={{ background: NAVY }}>{c.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1.4 }}>
            <FieldLabel>Sous-dossier de destination</FieldLabel>
            <select
              value={targetFolderId}
              onChange={(e) => setTargetFolderId(e.target.value)}
              disabled={!category || loading}
              data-testid="lib-filing-folder"
              className="glass-apple"
              style={{ width: '100%', padding: '11px 12px', borderRadius: 10, color: T1, fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5, outline: 'none', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}
            >
              {loading && <option style={{ background: NAVY }}>Chargement…</option>}
              {!loading && folders.length === 0 && <option style={{ background: NAVY }}>À la racine de la catégorie</option>}
              {!loading && folders.map((f) => {
                const path = [...(f.path || []), f.name].join(' / ');
                return <option key={f.id} value={f.id} style={{ background: NAVY }}>{path}</option>;
              })}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 6 }}>
          <button
            onClick={goBackToDoc}
            data-testid="lib-filing-reopen"
            className="glass-pill glass-apple-hover"
            style={{ padding: '10px 16px', borderRadius: 12, cursor: 'pointer', color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <ExternalLink size={12} /> Retourner sur le doc
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              data-testid="lib-filing-dismiss"
              className="glass-pill glass-apple-hover"
              style={{ padding: '10px 16px', borderRadius: 12, cursor: 'pointer', color: T3, fontFamily: 'Sora', fontWeight: 700, fontSize: 12 }}
            >Plus tard</button>
            <button
              onClick={fileNow}
              disabled={!targetFolderId || moving}
              data-testid="lib-filing-confirm"
              className="glass-apple-hover"
              style={{
                padding: '10px 20px', borderRadius: 12, cursor: targetFolderId && !moving ? 'pointer' : 'not-allowed',
                background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
                color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
                opacity: targetFolderId && !moving ? 1 : 0.5,
              }}
            >{moving ? 'Déplacement…' : 'Ranger'}</button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── AsaAiChatFab : bouton flottant IA ASA (bottom-right) + panel chat ───────
function AsaAiChatFab() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]); // {role: 'user'|'ai', text, matches?}
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    const q = query.trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setQuery('');
    setLoading(true);
    try {
      const { data } = await api.post('/library/v2/ai/search', { query: q });
      setMessages((m) => [...m, {
        role: 'ai',
        text: data.answer || 'Je n\'ai rien trouvé.',
        matches: data.matches || [],
        total: data.total_docs_indexed,
      }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'ai', text: `Erreur : ${e?.response?.data?.detail || e.message}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="asa-ai-chat-fab"
        title="IA ASA — trouve un doc dans ta biblio"
        style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 90,
          width: open ? 52 : 60, height: open ? 52 : 60, borderRadius: '50%',
          background: `linear-gradient(135deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
          border: '1px solid rgba(255,255,255,0.24)',
          boxShadow: '0 20px 50px -12px rgba(62,123,250,0.7), inset 0 1px 0 rgba(255,255,255,0.35)',
          cursor: 'pointer', color: '#F8FBFF',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {open ? <X size={20} strokeWidth={2.6} /> : <Sparkles size={22} strokeWidth={2.4} />}
      </button>
      {/* Chat panel */}
      {open && (
        <div
          data-testid="asa-ai-chat-panel"
          style={{
            position: 'fixed', bottom: 96, right: 28, zIndex: 89,
            width: 400, maxWidth: 'calc(100vw - 40px)', height: 520, maxHeight: '75vh',
            borderRadius: 20, overflow: 'hidden',
            background: 'linear-gradient(180deg, rgba(23, 34, 63, 0.98) 0%, rgba(15, 23, 48, 0.98) 100%)',
            border: '1px solid rgba(91,190,252,0.22)',
            backdropFilter: 'blur(28px) saturate(180%)',
            WebkitBackdropFilter: 'blur(28px) saturate(180%)',
            boxShadow: '0 30px 80px -20px rgba(3,7,20,0.85), inset 0 1px 0 rgba(255,255,255,0.06)',
            display: 'flex', flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '16px 18px', borderBottom: '1px solid rgba(91,190,252,0.14)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: `linear-gradient(135deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px -4px rgba(62,123,250,0.65)',
            }}><Sparkles size={16} color="#F8FBFF" strokeWidth={2.6} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5, color: T1 }}>IA ASA</div>
              <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3 }}>
                Cherche un doc, un dossier, un client…
              </div>
            </div>
          </div>
          {/* Messages */}
          <div
            ref={scrollRef}
            style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            {messages.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: T3, fontFamily: 'Sora', fontSize: 12, fontStyle: 'italic' }}>
                Exemple : « Où est rangée la note du brief Augustin ? »
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '86%', padding: '10px 13px', borderRadius: 12,
                  background: m.role === 'user' ? `linear-gradient(180deg, rgba(62,123,250,0.75), ${BLUE})` : 'rgba(91,190,252,0.08)',
                  border: m.role === 'user' ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(91,190,252,0.20)',
                  color: m.role === 'user' ? '#F8FBFF' : T1,
                  fontFamily: 'Sora', fontWeight: 500, fontSize: 12.5, lineHeight: 1.45,
                }}>
                  {m.text}
                </div>
                {m.role === 'ai' && m.matches && m.matches.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                    {m.matches.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => window.open(d.webViewLink, '_blank')}
                        className="glass-apple-hover"
                        data-testid={`asa-ai-match-${d.id}`}
                        style={{
                          padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                          background: 'rgba(66,133,244,0.10)', border: '1px solid rgba(66,133,244,0.28)',
                          color: T1, textAlign: 'left',
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}
                      >
                        <FileText size={14} color="#4285F4" />
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                          <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10, color: T3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            📁 {d.category_label} — {d.path}
                          </div>
                        </div>
                        <ExternalLink size={11} color={T3} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf: 'flex-start', color: T3, fontFamily: 'Sora', fontSize: 11.5, padding: '8px 12px' }}>
                <span style={{ opacity: 0.8 }}>IA ASA réfléchit…</span>
              </div>
            )}
          </div>
          {/* Input */}
          <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(91,190,252,0.14)', display: 'flex', gap: 8 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Pose ta question…"
              data-testid="asa-ai-chat-input"
              disabled={loading}
              className="glass-apple"
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 12,
                color: T1, outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 13,
              }}
            />
            <button
              onClick={send}
              disabled={!query.trim() || loading}
              data-testid="asa-ai-chat-send"
              style={{
                padding: '10px 16px', borderRadius: 12,
                cursor: query.trim() && !loading ? 'pointer' : 'not-allowed',
                background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
                color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
                opacity: query.trim() && !loading ? 1 : 0.5,
              }}
            >Envoyer</button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── MappingModal : lier chaque catégorie ASA à un dossier Drive ─────────────
function MappingModal({ onClose, isFounder }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rootFolders, setRootFolders] = useState([]);
  const [mapping, setMapping] = useState({}); // { category: {drive_folder_id, drive_folder_name} }
  const [categories, setCategories] = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [foldersRes, mappingRes] = await Promise.all([
        api.get('/library/v2/drive/root-folders'),
        api.get('/library/v2/drive/mapping'),
      ]);
      setRootFolders(foldersRes.data.folders || []);
      setMapping(mappingRes.data.mapping || {});
      setCategories(mappingRes.data.categories || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setError(detail);
      if (e?.response?.status === 401) toast.error('Google Drive déconnecté');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const normalize = (s) => (s || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

  const findSuggestion = (catKey) => {
    const target = normalize(catKey);
    return rootFolders.find((f) => normalize(f.name) === target)
        || rootFolders.find((f) => normalize(f.name).includes(target.split('_')[0]));
  };

  const setForCategory = async (catKey, folder) => {
    try {
      await api.post('/library/v2/drive/mapping', {
        category: catKey,
        drive_folder_id: folder.id,
        drive_folder_name: folder.name,
      });
      setMapping((m) => ({
        ...m,
        [catKey]: { drive_folder_id: folder.id, drive_folder_name: folder.name },
      }));
      toast.success(`${catKey} ↔ ${folder.name}`);
    } catch (e) {
      toast.error(`Erreur: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const unmap = async (catKey) => {
    try {
      await api.delete(`/library/v2/drive/mapping/${catKey}`);
      setMapping((m) => {
        const c = { ...m };
        delete c[catKey];
        return c;
      });
      toast.success('Mapping supprimé');
    } catch (e) {
      toast.error(`Erreur: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const autoMapAll = async () => {
    let matched = 0;
    for (const c of categories) {
      if (mapping[c.key]) continue;
      const sug = findSuggestion(c.label) || findSuggestion(c.key);
      if (sug) {
        await setForCategory(c.key, sug);
        matched++;
      }
    }
    toast.success(`${matched} catégorie(s) mappée(s) automatiquement`);
  };

  return (
    <ModalShell onClose={onClose} title="Mapping Bibliothèque ASA ↔ Google Drive" testId="lib-mapping-modal" wide>
      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: T3, fontFamily: 'Sora', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: 22,
            border: `3px solid rgba(91,190,252,0.16)`,
            borderTopColor: BLUE_LIGHT,
            animation: 'spin 0.9s linear infinite',
          }} />
          <div>Chargement des dossiers Drive…</div>
          <div style={{ fontSize: 11, color: T3, opacity: 0.7 }}>Ça peut prendre 5-10 sec la première fois</div>
        </div>
      )}
      {!loading && error && (
        <div style={{ padding: 30, textAlign: 'center' }}>
          <div style={{ fontFamily: 'Sora', fontSize: 13, color: '#FF7A7A', marginBottom: 12 }}>{error}</div>
          <button onClick={load} className="glass-pill glass-apple-hover" style={{ padding: '8px 16px', borderRadius: 10, cursor: 'pointer', color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 12 }}>Réessayer</button>
        </div>
      )}
      {!loading && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: 'Sora', fontSize: 12, color: T3, maxWidth: 520 }}>
              Pour chaque catégorie ASA, choisis le dossier Drive correspondant dans la racine `ASA Library`.
              Tous les Google Docs de ce dossier seront visibles dans ASA.
            </div>
            <button
              onClick={autoMapAll}
              data-testid="lib-automap-btn"
              className="glass-pill glass-apple-hover"
              style={{
                padding: '9px 14px', borderRadius: 12, cursor: 'pointer',
                color: BLUE_LIGHT, fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            ><Sparkles size={13} /> Auto-mapper</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
            {categories.map((c) => {
              const m = mapping[c.key];
              const suggestion = !m && findSuggestion(c.label);
              return (
                <div
                  key={c.key}
                  data-testid={`lib-mapping-row-${c.key}`}
                  className="glass-apple"
                  style={{
                    padding: 14, borderRadius: 14,
                    display: 'flex', alignItems: 'center', gap: 14,
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                    background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}><Folder size={18} color="#F8FBFF" strokeWidth={2.4} /></div>
                  <div style={{ minWidth: 180 }}>
                    <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 14, color: T1 }}>{c.label}</div>
                    <div style={{ fontFamily: 'Sora', fontSize: 11, color: T3, marginTop: 2 }}>catégorie ASA</div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ChevronRight size={16} color={T3} />
                    <select
                      value={m?.drive_folder_id || ''}
                      onChange={(e) => {
                        const fid = e.target.value;
                        if (!fid) { unmap(c.key); return; }
                        const folder = rootFolders.find((f) => f.id === fid);
                        if (folder) setForCategory(c.key, folder);
                      }}
                      data-testid={`lib-mapping-select-${c.key}`}
                      className="glass-apple"
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: 10,
                        color: T1, fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5,
                        outline: 'none', cursor: 'pointer',
                        appearance: 'none', WebkitAppearance: 'none',
                        background: 'rgba(20,29,66,0.62)',
                      }}
                    >
                      <option value="" style={{ background: NAVY }}>— Non lié —</option>
                      {rootFolders.map((f) => (
                        <option key={f.id} value={f.id} style={{ background: NAVY }}>{f.name}</option>
                      ))}
                    </select>
                    {suggestion && !m && (
                      <button
                        onClick={() => setForCategory(c.key, suggestion)}
                        data-testid={`lib-mapping-suggest-${c.key}`}
                        className="glass-pill glass-apple-hover"
                        style={{
                          padding: '8px 12px', borderRadius: 10, cursor: 'pointer',
                          color: BLUE_LIGHT, fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
                          whiteSpace: 'nowrap',
                        }}
                      >Utiliser « {suggestion.name} »</button>
                    )}
                  </div>
                  {m && (
                    <button
                      onClick={() => unmap(c.key)}
                      data-testid={`lib-mapping-unmap-${c.key}`}
                      title="Délier"
                      style={{
                        padding: 8, borderRadius: 8, cursor: 'pointer',
                        background: 'transparent', border: '1px solid rgba(255,122,122,0.30)',
                        color: '#FF7A7A',
                      }}
                    ><X size={12} /></button>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              onClick={onClose}
              data-testid="lib-mapping-done-btn"
              className="glass-apple-hover"
              style={{
                padding: '11px 22px', borderRadius: 12, cursor: 'pointer',
                background: `linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(62,123,250,0.75) 60%, ${BLUE} 100%)`,
                color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
              }}
            >Terminé</button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ─── Fichiers stub ───────────────────────────────────────────────────────────
function FilesStub() {
  return (
    <div style={{ padding: '80px 32px', textAlign: 'center', background: SURFACE, borderRadius: 16, border: `1px solid ${BORDER}` }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
        background: 'rgba(62,123,250,0.14)', border: `1px solid ${BORDER_S}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}><Folder size={28} color={BLUE_LIGHT} /></div>
      <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 20, color: T1, marginBottom: 8 }}>Fichiers</div>
      <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 14, color: T3, maxWidth: 480, margin: '0 auto' }}>
        Cet onglet connectera directement Google Drive : les dossiers et fichiers seront gérés là-bas et affichés ici de façon organisée. À développer plus tard.
      </div>
    </div>
  );
}

// ─── Shared modal shell + inputs ─────────────────────────────────────────────
function ModalShell({ onClose, title, testId, wide, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(3,7,20,0.68)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        animation: 'fadeIn 0.15s cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
        style={{
          width: '100%', maxWidth: wide ? 720 : 520,
          maxHeight: '90vh', overflowY: 'auto',
          // Iter 62ed — fond BLEU SOLIDE (plus de transparent, cf Rayan msg 2026-02-13)
          background: 'linear-gradient(180deg, #17223F 0%, #0F1730 100%)',
          border: '1px solid rgba(91,190,252,0.20)',
          borderRadius: 22, padding: 26,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10), 0 30px 70px -18px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.03)',
          // Animation plus rapide (0.25s au lieu de 0.75s)
          animation: 'blurUp 0.28s cubic-bezier(0.22, 1, 0.36, 1) both',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 19, color: T1, margin: 0, flex: 1, letterSpacing: '-0.01em' }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              cursor: 'pointer', color: T2, padding: 8, borderRadius: 10,
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.10)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
          >
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{children}</div>
  );
}

const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: 11,
  background: 'rgba(11, 19, 48, 0.60)',
  border: '1px solid rgba(91,190,252,0.18)', color: T1,
  outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 13,
  transition: 'border-color 0.15s',
};

const btnPrimary = {
  padding: '11px 20px', borderRadius: 11, cursor: 'pointer',
  background: `linear-gradient(180deg, rgba(255,255,255,0.15) 0%, ${BLUE} 100%)`,
  color: '#F8FBFF', border: '1px solid rgba(255,255,255,0.20)',
  fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
  boxShadow: '0 12px 26px -10px rgba(62,123,250,0.65), inset 0 1px 0 rgba(255,255,255,0.25)',
  transition: 'transform 0.15s',
};

const btnSecondary = {
  padding: '11px 20px', borderRadius: 11, cursor: 'pointer',
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: T2,
  fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
  transition: 'background 0.15s',
};
